// Complete AI Ontology Data - All 12 Layers, Sub-Layers, and Components

export const AI_ONTOLOGY = {
  phases: [
    {
      id: 'phase1',
      name: 'Physical & Substrate Layer',
      description: 'The physical hardware that executes instructions and stores state',
      icon: '🔧',
      layers: [
        {
          id: 'layer1',
          name: 'Physical Substrate & Compute',
          keyQuestion: 'The physical hardware that executes instructions and stores state',
          subLayers: [
            {
              id: 'sl1.1',
              name: 'Processing Units',
              meaning: 'The silicon (or alternative) that performs arithmetic and logic operations',
              components: [
                {
                  id: 'comp1.1.1',
                  name: 'CPU (x86)',
                  purpose: 'General-purpose sequential processing',
                  function: 'Execute complex instructions, manage I/O operations',
                  cost: { low: 200, high: 5000, unit: 'per unit' },
                  compatibility: ['all', 'general', 'essential'],
                  tags: ['general', 'essential', 'x86', 'intel', 'amd'],
                  icon: '🖥️',
                  description: 'x86 architecture CPU from Intel or AMD',
                  realWorldExample: 'Intel Xeon, AMD EPYC'
                },
                {
                  id: 'comp1.1.2',
                  name: 'CPU (ARM)',
                  purpose: 'Energy-efficient processing',
                  function: 'Execute instructions with lower power consumption',
                  cost: { low: 50, high: 2000, unit: 'per unit' },
                  compatibility: ['edge', 'mobile', 'embedded', 'iot'],
                  tags: ['arm', 'efficient', 'mobile', 'edge'],
                  icon: '📱',
                  description: 'ARM architecture CPU for mobile and edge devices',
                  realWorldExample: 'ARM Cortex, Apple M-series'
                },
                {
                  id: 'comp1.1.3',
                  name: 'CPU (RISC-V)',
                  purpose: 'Open-source processor architecture',
                  function: 'Execute instructions with open ISA',
                  cost: { low: 10, high: 500, unit: 'per unit' },
                  compatibility: ['embedded', 'research', 'custom', 'iot'],
                  tags: ['risc-v', 'open-source', 'embedded'],
                  icon: '🔓',
                  description: 'Open-source RISC-V processor',
                  realWorldExample: 'SiFive, ESP32-C3'
                },
                {
                  id: 'comp1.1.4',
                  name: 'GPU (NVIDIA)',
                  purpose: 'Parallel processing for AI/ML',
                  function: 'Accelerate deep learning training and inference',
                  cost: { low: 300, high: 40000, unit: 'per unit' },
                  compatibility: ['deep-learning', 'cuda', 'training', 'inference', 'llm'],
                  tags: ['gpu', 'nvidia', 'cuda', 'parallel', 'training'],
                  icon: '🎮',
                  description: 'NVIDIA GPU with CUDA support',
                  realWorldExample: 'NVIDIA A100, H100, RTX 4090'
                },
                {
                  id: 'comp1.1.5',
                  name: 'GPU (AMD)',
                  purpose: 'Parallel processing for AI/ML',
                  function: 'Accelerate deep learning with ROCm',
                  cost: { low: 200, high: 15000, unit: 'per unit' },
                  compatibility: ['deep-learning', 'rocm', 'training', 'inference'],
                  tags: ['gpu', 'amd', 'rocm', 'parallel'],
                  icon: '🎮',
                  description: 'AMD GPU with ROCm support',
                  realWorldExample: 'AMD Instinct MI250, Radeon Pro'
                },
                {
                  id: 'comp1.1.6',
                  name: 'TPU (Google)',
                  purpose: 'Tensor processing for neural networks',
                  function: 'Specialized matrix multiplication for ML',
                  cost: { low: 1000, high: 10000, unit: 'per unit' },
                  compatibility: ['tensorflow', 'neural-networks', 'google-cloud', 'training'],
                  tags: ['tpu', 'google', 'tensor', 'specialized'],
                  icon: '⚡',
                  description: 'Google Tensor Processing Unit',
                  realWorldExample: 'Google TPU v4, Edge TPU'
                },
                {
                  id: 'comp1.1.7',
                  name: 'NPU (Apple Neural Engine)',
                  purpose: 'On-device AI acceleration',
                  function: 'Accelerate neural networks on Apple devices',
                  cost: { low: 0, high: 0, unit: 'integrated' },
                  compatibility: ['apple', 'mobile', 'edge', 'on-device', 'ios'],
                  tags: ['npu', 'apple', 'mobile', 'edge'],
                  icon: '🍎',
                  description: 'Apple Neural Engine for on-device AI',
                  realWorldExample: 'A17 Pro, M3 chips'
                },
                {
                  id: 'comp1.1.8',
                  name: 'FPGA',
                  purpose: 'Reconfigurable hardware acceleration',
                  function: 'Custom logic implementation for specific algorithms',
                  cost: { low: 100, high: 10000, unit: 'per unit' },
                  compatibility: ['real-time', 'low-latency', 'edge', 'custom-hardware'],
                  tags: ['fpga', 'reconfigurable', 'hardware', 'real-time'],
                  icon: '🔧',
                  description: 'Field-Programmable Gate Array',
                  realWorldExample: 'Xilinx Alveo, Intel Stratix'
                },
                {
                  id: 'comp1.1.9',
                  name: 'ASIC',
                  purpose: 'Application-specific integrated circuit',
                  function: 'Highly optimized for specific AI tasks',
                  cost: { low: 100000, high: 1000000, unit: 'per design' },
                  compatibility: ['bitcoin-mining', 'custom-ai', 'high-volume'],
                  tags: ['asic', 'custom', 'specialized', 'high-volume'],
                  icon: '🔩',
                  description: 'Custom-designed chip for specific AI workloads',
                  realWorldExample: 'Bitmain ASICs, Google TPU (custom ASIC)'
                },
                {
                  id: 'comp1.1.10',
                  name: 'Neuromorphic Chip',
                  purpose: 'Brain-inspired computing',
                  function: 'Mimic neural structures for efficient AI',
                  cost: { low: 1000, high: 50000, unit: 'per unit' },
                  compatibility: ['spiking-neural-networks', 'research', 'low-power'],
                  tags: ['neuromorphic', 'spiking', 'brain-inspired', 'research'],
                  icon: '🧠',
                  description: 'Neuromorphic processor mimicking brain function',
                  realWorldExample: 'Intel Loihi, IBM TrueNorth'
                },
                {
                  id: 'comp1.1.11',
                  name: 'Quantum Processor',
                  purpose: 'Quantum computation for specific problems',
                  function: 'Execute quantum algorithms',
                  cost: { low: 1000000, high: 50000000, unit: 'per system' },
                  compatibility: ['quantum-ml', 'optimization', 'cryptography', 'research'],
                  tags: ['quantum', 'experimental', 'research', 'qubits'],
                  icon: '🔮',
                  description: 'Quantum processing unit for quantum computing',
                  realWorldExample: 'IBM Osprey, Google Sycamore'
                },
                {
                  id: 'comp1.1.12',
                  name: 'Analog Computer',
                  purpose: 'Continuous signal processing',
                  function: 'Process continuous signals directly',
                  cost: { low: 1000, high: 100000, unit: 'per system' },
                  compatibility: ['differential-equations', 'control-systems', 'specialized'],
                  tags: ['analog', 'continuous', 'specialized', 'legacy'],
                  icon: '📊',
                  description: 'Analog computer for continuous computation',
                  realWorldExample: 'Mythic AI analog matrix processors'
                }
              ]
            },
            {
              id: 'sl1.2',
              name: 'Volatile Memory',
              meaning: 'Fast, temporary storage for active processes and model weights',
              components: [
                {
                  id: 'comp1.2.1',
                  name: 'DRAM',
                  purpose: 'Main system memory',
                  function: 'Store active data and program instructions',
                  cost: { low: 50, high: 5000, unit: 'per module' },
                  compatibility: ['all', 'essential', 'system'],
                  tags: ['memory', 'dram', 'essential', 'temporary'],
                  icon: '💾',
                  description: 'Dynamic Random Access Memory',
                  realWorldExample: 'DDR4, DDR5 modules'
                },
                {
                  id: 'comp1.2.2',
                  name: 'SRAM',
                  purpose: 'Cache memory',
                  function: 'Store frequently accessed data',
                  cost: { low: 1, high: 100, unit: 'per chip' },
                  compatibility: ['all-cpus', 'cache', 'embedded'],
                  tags: ['sram', 'cache', 'fast', 'integrated'],
                  icon: '⚡',
                  description: 'Static RAM for CPU cache',
                  realWorldExample: 'L1/L2/L3 cache in processors'
                },
                {
                  id: 'comp1.2.3',
                  name: 'HBM (High Bandwidth Memory)',
                  purpose: 'High-speed memory for accelerators',
                  function: 'Provide high bandwidth to GPUs and AI accelerators',
                  cost: { low: 500, high: 10000, unit: 'per stack' },
                  compatibility: ['gpu', 'accelerator', 'high-performance'],
                  tags: ['hbm', 'high-bandwidth', 'accelerator', 'stacked'],
                  icon: '🚀',
                  description: 'High Bandwidth Memory for AI accelerators',
                  realWorldExample: 'HBM2e, HBM3 in NVIDIA H100'
                },
                {
                  id: 'comp1.2.4',
                  name: 'CPU Cache (L1/L2/L3)',
                  purpose: 'Ultra-fast processor cache',
                  function: 'Store frequently accessed instructions and data',
                  cost: { low: 0, high: 0, unit: 'integrated' },
                  compatibility: ['all-cpus', 'integrated'],
                  tags: ['cache', 'integrated', 'cpu'],
                  icon: '🎯',
                  description: 'Multi-level CPU cache hierarchy',
                  realWorldExample: 'Integrated in all modern CPUs'
                },
                {
                  id: 'comp1.2.5',
                  name: 'Register Files',
                  purpose: 'Fastest storage in processor',
                  function: 'Store operands for immediate CPU operations',
                  cost: { low: 0, high: 0, unit: 'integrated' },
                  compatibility: ['all-cpus', 'integrated'],
                  tags: ['register', 'integrated', 'cpu'],
                  icon: '📝',
                  description: 'CPU registers for immediate data access',
                  realWorldExample: 'General-purpose and SIMD registers'
                }
              ]
            },
            {
              id: 'sl1.3',
              name: 'Non-Volatile Storage',
              meaning: 'Permanent storage for data, model artifacts, and checkpoints',
              components: [
                {
                  id: 'comp1.3.1',
                  name: 'SSD (NVMe)',
                  purpose: 'High-speed persistent storage',
                  function: 'Store data with fast read/write speeds',
                  cost: { low: 100, high: 5000, unit: 'per TB' },
                  compatibility: ['all', 'high-performance', 'storage'],
                  tags: ['ssd', 'nvme', 'storage', 'fast'],
                  icon: '💿',
                  description: 'NVMe SSD for high-speed data access',
                  realWorldExample: 'Samsung 990 Pro, Intel Optane'
                },
                {
                  id: 'comp1.3.2',
                  name: 'SSD (SATA)',
                  purpose: 'Cost-effective solid-state storage',
                  function: 'Store data with good performance',
                  cost: { low: 50, high: 2000, unit: 'per TB' },
                  compatibility: ['all', 'storage', 'budget'],
                  tags: ['ssd', 'sata', 'storage'],
                  icon: '💿',
                  description: 'SATA SSD for balanced performance',
                  realWorldExample: 'Samsung 870 EVO, Crucial MX500'
                },
                {
                  id: 'comp1.3.3',
                  name: 'HDD',
                  purpose: 'Bulk storage for large datasets',
                  function: 'Cost-effective storage for large data volumes',
                  cost: { low: 50, high: 500, unit: 'per TB' },
                  compatibility: ['data-lake', 'backup', 'archive'],
                  tags: ['hdd', 'bulk', 'cheap', 'storage'],
                  icon: '📀',
                  description: 'Hard Disk Drive for bulk storage',
                  realWorldExample: 'Western Digital Red Pro, Seagate Exos'
                },
                {
                  id: 'comp1.3.4',
                  name: 'Flash Storage',
                  purpose: 'Portable and embedded storage',
                  function: 'Store data in flash memory',
                  cost: { low: 10, high: 500, unit: 'per unit' },
                  compatibility: ['mobile', 'embedded', 'portable', 'iot'],
                  tags: ['flash', 'portable', 'embedded'],
                  icon: '💾',
                  description: 'Flash storage for portable devices',
                  realWorldExample: 'SD cards, USB drives, eMMC'
                },
                {
                  id: 'comp1.3.5',
                  name: 'Tape Drive',
                  purpose: 'Long-term archival storage',
                  function: 'Store petabytes of cold data',
                  cost: { low: 5, high: 50, unit: 'per TB' },
                  compatibility: ['archive', 'compliance', 'backup', 'cold-storage'],
                  tags: ['tape', 'archival', 'cold-storage', 'cheap'],
                  icon: '📼',
                  description: 'Magnetic tape for long-term data retention',
                  realWorldExample: 'IBM TS4500, Oracle StorageTek'
                },
                {
                  id: 'comp1.3.6',
                  name: 'Optical Storage',
                  purpose: 'Read-only and write-once storage',
                  function: 'Store data on optical media',
                  cost: { low: 1, high: 100, unit: 'per unit' },
                  compatibility: ['archive', 'distribution', 'legacy'],
                  tags: ['optical', 'blu-ray', 'dvd', 'archive'],
                  icon: '📀',
                  description: 'Optical storage media',
                  realWorldExample: 'Blu-ray, DVD, Archival Disc'
                }
              ]
            },
            {
              id: 'sl1.4',
              name: 'Networking Fabric',
              meaning: 'Physical and data-link layer protocols for data movement',
              components: [
                {
                  id: 'comp1.4.1',
                  name: 'Ethernet',
                  purpose: 'Standard network connectivity',
                  function: 'Connect servers and storage systems',
                  cost: { low: 10, high: 10000, unit: 'per port' },
                  compatibility: ['all', 'networking', 'standard'],
                  tags: ['ethernet', 'network', 'standard', 'essential'],
                  icon: '🌐',
                  description: 'Standard Ethernet networking',
                  realWorldExample: '1GbE, 10GbE, 100GbE'
                },
                {
                  id: 'comp1.4.2',
                  name: 'InfiniBand',
                  purpose: 'High-performance interconnect',
                  function: 'Low-latency communication for HPC clusters',
                  cost: { low: 500, high: 50000, unit: 'per port' },
                  compatibility: ['hpc', 'gpu-cluster', 'training', 'low-latency'],
                  tags: ['infiniband', 'hpc', 'low-latency', 'cluster'],
                  icon: '🔗',
                  description: 'High-speed interconnect for distributed training',
                  realWorldExample: 'NVIDIA Mellanox InfiniBand, Intel Omni-Path'
                },
                {
                  id: 'comp1.4.3',
                  name: 'PCIe Bus',
                  purpose: 'Internal component interconnect',
                  function: 'Connect CPU, GPU, and peripherals',
                  cost: { low: 0, high: 0, unit: 'integrated' },
                  compatibility: ['all-computers', 'internal'],
                  tags: ['pcie', 'internal', 'bus', 'interconnect'],
                  icon: '🔌',
                  description: 'PCI Express bus for internal components',
                  realWorldExample: 'PCIe 4.0, PCIe 5.0'
                },
                {
                  id: 'comp1.4.4',
                  name: 'NVLink',
                  purpose: 'GPU-to-GPU high-speed interconnect',
                  function: 'Enable high-bandwidth GPU communication',
                  cost: { low: 0, high: 0, unit: 'integrated' },
                  compatibility: ['nvidia-gpu', 'multi-gpu', 'training'],
                  tags: ['nvlink', 'gpu', 'interconnect', 'nvidia'],
                  icon: '⚡',
                  description: 'NVIDIA proprietary GPU interconnect',
                  realWorldExample: 'NVLink 4.0 in HGX systems'
                },
                {
                  id: 'comp1.4.5',
                  name: '5G/Wi-Fi Antennas',
                  purpose: 'Wireless connectivity',
                  function: 'Enable wireless data transmission',
                  cost: { low: 10, high: 1000, unit: 'per unit' },
                  compatibility: ['mobile', 'iot', 'wireless', 'edge'],
                  tags: ['5g', 'wifi', 'wireless', 'mobile'],
                  icon: '📡',
                  description: 'Wireless communication antennas',
                  realWorldExample: '5G modules, Wi-Fi 6E adapters'
                },
                {
                  id: 'comp1.4.6',
                  name: 'Fiber Optics',
                  purpose: 'Long-distance high-speed connectivity',
                  function: 'Transmit data over long distances',
                  cost: { low: 100, high: 10000, unit: 'per link' },
                  compatibility: ['data-center', 'long-distance', 'high-bandwidth'],
                  tags: ['fiber', 'optical', 'long-distance', 'high-bandwidth'],
                  icon: '🔦',
                  description: 'Fiber optic networking for long distances',
                  realWorldExample: 'Single-mode fiber, multi-mode fiber'
                }
              ]
            },
            {
              id: 'sl1.5',
              name: 'Power & Thermal',
              meaning: 'Energy and cooling management systems',
              components: [
                {
                  id: 'comp1.5.1',
                  name: 'Power Supply Units',
                  purpose: 'Provide electrical power',
                  function: 'Convert AC to DC power for components',
                  cost: { low: 50, high: 5000, unit: 'per unit' },
                  compatibility: ['all', 'essential', 'power'],
                  tags: ['power', 'psu', 'essential'],
                  icon: '🔌',
                  description: 'Power Supply Unit for computer systems',
                  realWorldExample: 'Corsair, Seasonic PSUs'
                },
                {
                  id: 'comp1.5.2',
                  name: 'Voltage Regulators',
                  purpose: 'Stabilize voltage levels',
                  function: 'Maintain consistent voltage for components',
                  cost: { low: 1, high: 100, unit: 'per unit' },
                  compatibility: ['all', 'power', 'integrated'],
                  tags: ['voltage', 'regulator', 'power'],
                  icon: '⚡',
                  description: 'Voltage regulation for stable operation',
                  realWorldExample: 'Onboard VRMs, standalone regulators'
                },
                {
                  id: 'comp1.5.3',
                  name: 'Liquid Cooling',
                  purpose: 'Efficient heat dissipation',
                  function: 'Cool high-density compute racks',
                  cost: { low: 1000, high: 100000, unit: 'per rack' },
                  compatibility: ['gpu-cluster', 'hpc', 'data-center', 'high-density'],
                  tags: ['cooling', 'liquid', 'efficiency', 'density'],
                  icon: '❄️',
                  description: 'Liquid cooling for high-density compute',
                  realWorldExample: 'CoolIT, GRC immersion cooling'
                },
                {
                  id: 'comp1.5.4',
                  name: 'Air Cooling',
                  purpose: 'Standard heat dissipation',
                  function: 'Cool individual servers and workstations',
                  cost: { low: 10, high: 500, unit: 'per server' },
                  compatibility: ['all', 'standard', 'cooling'],
                  tags: ['cooling', 'air', 'standard'],
                  icon: '🌬️',
                  description: 'Traditional air cooling with fans',
                  realWorldExample: 'Noctua fans, Dell server cooling'
                },
                {
                  id: 'comp1.5.5',
                  name: 'Heat Sinks',
                  purpose: 'Passive heat dissipation',
                  function: 'Dissipate heat from components',
                  cost: { low: 5, high: 200, unit: 'per unit' },
                  compatibility: ['all', 'cooling', 'passive'],
                  tags: ['heat-sink', 'cooling', 'passive'],
                  icon: '🔩',
                  description: 'Passive heat dissipation devices',
                  realWorldExample: 'Aluminum and copper heat sinks'
                },
                {
                  id: 'comp1.5.6',
                  name: 'Uninterruptible Power Supplies',
                  purpose: 'Power backup',
                  function: 'Provide backup power during outages',
                  cost: { low: 100, high: 10000, unit: 'per unit' },
                  compatibility: ['data-center', 'critical-systems', 'reliability'],
                  tags: ['ups', 'backup', 'power', 'reliability'],
                  icon: '🔋',
                  description: 'UPS for continuous power supply',
                  realWorldExample: 'APC Smart-UPS, Eaton 9PX'
                }
              ]
            }
          ]
        },
        {
          id: 'layer2',
          name: 'Environmental Interface (Sensors & Actuators)',
          keyQuestion: 'The boundary between digital and analog world',
          subLayers: [
            {
              id: 'sl2.1',
              name: 'Physical Sensors (Input)',
              meaning: 'Devices that convert physical phenomena into digital data',
              components: [
                {
                  id: 'comp2.1.1',
                  name: 'Camera (Visible Light)',
                  purpose: 'Capture visual information',
                  function: 'Convert light into digital images',
                  cost: { low: 10, high: 5000, unit: 'per unit' },
                  compatibility: ['computer-vision', 'surveillance', 'robotics', 'images'],
                  tags: ['camera', 'vision', 'input', 'essential'],
                  icon: '📷',
                  description: 'Standard RGB camera for computer vision',
                  realWorldExample: 'Intel RealSense, Basler ace'
                },
                {
                  id: 'comp2.1.2',
                  name: 'Camera (Infrared)',
                  purpose: 'Capture thermal information',
                  function: 'Detect infrared radiation',
                  cost: { low: 200, high: 30000, unit: 'per unit' },
                  compatibility: ['security', 'industrial', 'medical', 'thermal'],
                  tags: ['camera', 'infrared', 'thermal', 'specialized'],
                  icon: '🌡️',
                  description: 'Infrared camera for thermal detection',
                  realWorldExample: 'FLIR One, Seek Thermal'
                },
                {
                  id: 'comp2.1.3',
                  name: 'LIDAR',
                  purpose: '3D depth sensing',
                  function: 'Measure distance using laser pulses',
                  cost: { low: 100, high: 75000, unit: 'per unit' },
                  compatibility: ['autonomous', '3d-mapping', 'robotics', 'depth'],
                  tags: ['lidar', '3d', 'depth', 'autonomous'],
                  icon: '📡',
                  description: 'Light Detection and Ranging for 3D perception',
                  realWorldExample: 'Velodyne Puck, Ouster OS1'
                },
                {
                  id: 'comp2.1.4',
                  name: 'Microphone',
                  purpose: 'Capture audio information',
                  function: 'Record sound for speech recognition',
                  cost: { low: 5, high: 1000, unit: 'per unit' },
                  compatibility: ['speech', 'audio-processing', 'assistant', 'sound'],
                  tags: ['microphone', 'audio', 'input', 'speech'],
                  icon: '🎤',
                  description: 'Audio capture device',
                  realWorldExample: 'Shure, Rode microphones'
                },
                {
                  id: 'comp2.1.5',
                  name: 'Thermometer',
                  purpose: 'Temperature measurement',
                  function: 'Measure ambient or object temperature',
                  cost: { low: 1, high: 100, unit: 'per unit' },
                  compatibility: ['iot', 'environmental', 'monitoring', 'temperature'],
                  tags: ['temperature', 'sensor', 'environmental'],
                  icon: '🌡️',
                  description: 'Temperature sensor',
                  realWorldExample: 'DS18B20, DHT22'
                },
                {
                  id: 'comp2.1.6',
                  name: 'Accelerometer',
                  purpose: 'Acceleration measurement',
                  function: 'Measure acceleration forces',
                  cost: { low: 1, high: 50, unit: 'per unit' },
                  compatibility: ['motion', 'wearables', 'mobile', 'vibration'],
                  tags: ['accelerometer', 'motion', 'sensor'],
                  icon: '📳',
                  description: 'Acceleration sensor',
                  realWorldExample: 'MPU-6050, ADXL345'
                },
                {
                  id: 'comp2.1.7',
                  name: 'Gyroscope',
                  purpose: 'Angular velocity measurement',
                  function: 'Measure rotation and orientation',
                  cost: { low: 1, high: 100, unit: 'per unit' },
                  compatibility: ['motion', 'drones', 'robotics', 'orientation'],
                  tags: ['gyroscope', 'motion', 'orientation'],
                  icon: '🔄',
                  description: 'Angular velocity sensor',
                  realWorldExample: 'MPU-6050, L3GD20'
                },
                {
                  id: 'comp2.1.8',
                  name: 'GPS',
                  purpose: 'Location tracking',
                  function: 'Determine geographic position',
                  cost: { low: 5, high: 100, unit: 'per unit' },
                  compatibility: ['location', 'navigation', 'tracking', 'autonomous'],
                  tags: ['gps', 'location', 'navigation'],
                  icon: '📍',
                  description: 'Global Positioning System receiver',
                  realWorldExample: 'u-blox NEO-6M, Trimble'
                },
                {
                  id: 'comp2.1.9',
                  name: 'Pressure Sensor',
                  purpose: 'Pressure measurement',
                  function: 'Measure atmospheric or fluid pressure',
                  cost: { low: 1, high: 100, unit: 'per unit' },
                  compatibility: ['environmental', 'industrial', 'altitude', 'weather'],
                  tags: ['pressure', 'sensor', 'environmental'],
                  icon: '🌪️',
                  description: 'Pressure measurement sensor',
                  realWorldExample: 'BMP280, MS5611'
                },
                {
                  id: 'comp2.1.10',
                  name: 'Chemical/Bio Sensor',
                  purpose: 'Chemical detection',
                  function: 'Detect chemical or biological agents',
                  cost: { low: 10, high: 1000, unit: 'per unit' },
                  compatibility: ['environmental', 'medical', 'safety', 'industrial'],
                  tags: ['chemical', 'bio', 'sensor', 'safety'],
                  icon: '🧪',
                  description: 'Chemical and biological sensor',
                  realWorldExample: 'MQ series gas sensors, biosensors'
                }
              ]
            },
            {
              id: 'sl2.2',
              name: 'Physical Actuators (Output)',
              meaning: 'Devices that convert digital commands into physical action',
              components: [
                {
                  id: 'comp2.2.1',
                  name: 'Servo Motor',
                  purpose: 'Precise position control',
                  function: 'Rotate to specific angles based on control signals',
                  cost: { low: 10, high: 1000, unit: 'per unit' },
                  compatibility: ['robotics', 'automation', 'cnc', 'control'],
                  tags: ['servo', 'motor', 'control', 'precision'],
                  icon: '⚙️',
                  description: 'Motor with closed-loop position feedback',
                  realWorldExample: 'Dynamixel, Hitec servos'
                },
                {
                  id: 'comp2.2.2',
                  name: 'Stepper Motor',
                  purpose: 'Precise incremental rotation',
                  function: 'Rotate in discrete steps',
                  cost: { low: 10, high: 500, unit: 'per unit' },
                  compatibility: ['cnc', '3d-printing', 'automation', 'positioning'],
                  tags: ['stepper', 'motor', 'precision', 'positioning'],
                  icon: '⚙️',
                  description: 'Motor that rotates in discrete steps',
                  realWorldExample: 'NEMA 17, NEMA 23 steppers'
                },
                {
                  id: 'comp2.2.3',
                  name: 'Hydraulic/Pneumatic Valves',
                  purpose: 'Fluid power control',
                  function: 'Control fluid flow for actuation',
                  cost: { low: 100, high: 10000, unit: 'per unit' },
                  compatibility: ['industrial', 'heavy-machinery', 'automation'],
                  tags: ['hydraulic', 'pneumatic', 'valve', 'industrial'],
                  icon: '🔧',
                  description: 'Fluid power control valves',
                  realWorldExample: 'Bosch Rexroth, Festo valves'
                },
                {
                  id: 'comp2.2.4',
                  name: 'Speaker',
                  purpose: 'Audio output',
                  function: 'Generate sound for user interaction',
                  cost: { low: 10, high: 10000, unit: 'per system' },
                  compatibility: ['assistant', 'notification', 'alert', 'audio-output'],
                  tags: ['speaker', 'audio', 'output'],
                  icon: '🔊',
                  description: 'Audio output device',
                  realWorldExample: 'Amazon Echo, Sonos speakers'
                },
                {
                  id: 'comp2.2.5',
                  name: 'Haptic Feedback Motor',
                  purpose: 'Tactile feedback',
                  function: 'Generate vibration for user feedback',
                  cost: { low: 1, high: 50, unit: 'per unit' },
                  compatibility: ['mobile', 'wearables', 'gaming', 'feedback'],
                  tags: ['haptic', 'vibration', 'feedback', 'tactile'],
                  icon: '📳',
                  description: 'Vibration motor for haptic feedback',
                  realWorldExample: 'Linear resonant actuators, ERM motors'
                },
                {
                  id: 'comp2.2.6',
                  name: 'LED/Screen',
                  purpose: 'Visual output',
                  function: 'Display visual information',
                  cost: { low: 10, high: 1000, unit: 'per unit' },
                  compatibility: ['display', 'visual', 'output', 'interface'],
                  tags: ['led', 'screen', 'display', 'visual'],
                  icon: '🖥️',
                  description: 'Visual display output',
                  realWorldExample: 'OLED, LCD, LED displays'
                }
              ]
            },
            {
              id: 'sl2.3',
              name: 'Digital Interfaces',
              meaning: 'Programmatic endpoints for interacting with external systems',
              components: [
                {
                  id: 'comp2.3.1',
                  name: 'REST API',
                  purpose: 'Standard web API integration',
                  function: 'Enable HTTP-based communication',
                  cost: { low: 0, high: 1000, unit: 'per integration' },
                  compatibility: ['all', 'web', 'api', 'integration'],
                  tags: ['api', 'rest', 'web', 'standard'],
                  icon: '🔌',
                  description: 'RESTful API for system integration',
                  realWorldExample: 'OpenAI API, Stripe API'
                },
                {
                  id: 'comp2.3.2',
                  name: 'Webhook',
                  purpose: 'Event-driven notifications',
                  function: 'Send HTTP callbacks on events',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['event-driven', 'integration', 'automation'],
                  tags: ['webhook', 'event', 'callback', 'automation'],
                  icon: '🪝',
                  description: 'HTTP callback for event notification',
                  realWorldExample: 'GitHub webhooks, Stripe webhooks'
                },
                {
                  id: 'comp2.3.3',
                  name: 'gRPC',
                  purpose: 'High-performance RPC framework',
                  function: 'Enable efficient service-to-service communication',
                  cost: { low: 0, high: 500, unit: 'per service' },
                  compatibility: ['microservices', 'internal', 'performance', 'rpc'],
                  tags: ['grpc', 'rpc', 'efficient', 'microservices'],
                  icon: '⚡',
                  description: 'gRPC for high-performance internal APIs',
                  realWorldExample: 'Google Cloud APIs, etcd'
                },
                {
                  id: 'comp2.3.4',
                  name: 'Bluetooth/BLE',
                  purpose: 'Short-range wireless',
                  function: 'Enable device-to-device communication',
                  cost: { low: 1, high: 50, unit: 'per unit' },
                  compatibility: ['iot', 'mobile', 'wearables', 'short-range'],
                  tags: ['bluetooth', 'ble', 'wireless', 'short-range'],
                  icon: '📶',
                  description: 'Bluetooth Low Energy connectivity',
                  realWorldExample: 'BLE modules, Bluetooth 5.0'
                },
                {
                  id: 'comp2.3.5',
                  name: 'USB',
                  purpose: 'Universal serial connection',
                  function: 'Connect peripherals and devices',
                  cost: { low: 1, high: 50, unit: 'per port' },
                  compatibility: ['all', 'peripheral', 'connection'],
                  tags: ['usb', 'connection', 'peripheral'],
                  icon: '🔌',
                  description: 'Universal Serial Bus interface',
                  realWorldExample: 'USB 3.0, USB-C, USB 4.0'
                },
                {
                  id: 'comp2.3.6',
                  name: 'File Upload',
                  purpose: 'File transfer interface',
                  function: 'Accept file input from users',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['web', 'batch', 'user-input'],
                  tags: ['file', 'upload', 'input', 'web'],
                  icon: '📁',
                  description: 'File upload interface',
                  realWorldExample: 'HTML file input, drag-and-drop'
                },
                {
                  id: 'comp2.3.7',
                  name: 'Terminal/CLI Input',
                  purpose: 'Command-line interaction',
                  function: 'Accept text commands from users',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['developer', 'automation', 'scripting'],
                  tags: ['cli', 'terminal', 'command-line', 'developer'],
                  icon: '⌨️',
                  description: 'Command-line interface',
                  realWorldExample: 'Bash, PowerShell, Python REPL'
                },
                {
                  id: 'comp2.3.8',
                  name: 'UI (Mouse/Keyboard/Touch)',
                  purpose: 'Graphical user interaction',
                  function: 'Accept user input via GUI',
                  cost: { low: 10, high: 500, unit: 'per unit' },
                  compatibility: ['desktop', 'web', 'interactive'],
                  tags: ['ui', 'mouse', 'keyboard', 'touch'],
                  icon: '🖱️',
                  description: 'Graphical user interface input',
                  realWorldExample: 'Mouse, keyboard, touchscreen'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase2',
      name: 'Informational Layer',
      description: 'The raw material of intelligence - data',
      icon: '💾',
      layers: [
        {
          id: 'layer3',
          name: 'Data Acquisition & Generation',
          keyQuestion: 'The process of bringing raw information into the system',
          subLayers: [
            {
              id: 'sl3.1',
              name: 'Streaming Ingestion',
              meaning: 'Capturing continuous, high-frequency, unbounded data in real-time',
              components: [
                {
                  id: 'comp3.1.1',
                  name: 'Apache Kafka',
                  purpose: 'Distributed event streaming',
                  function: 'Publish and subscribe to streams of records',
                  cost: { low: 0, high: 5000, unit: 'per month' },
                  compatibility: ['streaming', 'event-driven', 'big-data', 'distributed'],
                  tags: ['kafka', 'streaming', 'distributed', 'events'],
                  icon: '📨',
                  description: 'Distributed event streaming platform',
                  realWorldExample: 'LinkedIn, Netflix, Uber use Kafka'
                },
                {
                  id: 'comp3.1.2',
                  name: 'AWS Kinesis',
                  purpose: 'Managed streaming service',
                  function: 'Process and analyze streaming data',
                  cost: { low: 100, high: 10000, unit: 'per month' },
                  compatibility: ['aws', 'streaming', 'managed', 'cloud'],
                  tags: ['aws', 'kinesis', 'streaming', 'managed'],
                  icon: '☁️',
                  description: 'Amazon managed streaming data service',
                  realWorldExample: 'Amazon Kinesis Data Streams'
                },
                {
                  id: 'comp3.1.3',
                  name: 'MQTT Broker',
                  purpose: 'Lightweight IoT messaging',
                  function: 'Connect IoT devices with minimal overhead',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['iot', 'edge', 'sensors', 'lightweight'],
                  tags: ['mqtt', 'iot', 'lightweight', 'messaging'],
                  icon: '📡',
                  description: 'Message broker for IoT devices',
                  realWorldExample: 'Mosquitto, HiveMQ'
                },
                {
                  id: 'comp3.1.4',
                  name: 'WebSocket',
                  purpose: 'Real-time bidirectional communication',
                  function: 'Enable persistent connections for streaming data',
                  cost: { low: 0, high: 500, unit: 'per connection' },
                  compatibility: ['real-time', 'streaming', 'web', 'bidirectional'],
                  tags: ['websocket', 'real-time', 'streaming', 'web'],
                  icon: '🔌',
                  description: 'WebSocket for real-time data streaming',
                  realWorldExample: 'Socket.io, WebSocket API'
                },
                {
                  id: 'comp3.1.5',
                  name: 'Telemetry Agent',
                  purpose: 'Collect system metrics',
                  function: 'Gather and forward system telemetry',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['monitoring', 'metrics', 'system', 'observability'],
                  tags: ['telemetry', 'agent', 'metrics', 'monitoring'],
                  icon: '📊',
                  description: 'Telemetry collection agent',
                  realWorldExample: 'Telegraf, Fluentd, Logstash'
                }
              ]
            },
            {
              id: 'sl3.2',
              name: 'Batch Extraction',
              meaning: 'Pulling bounded, historical data at scheduled intervals',
              components: [
                {
                  id: 'comp3.2.1',
                  name: 'SQL SELECT Queries',
                  purpose: 'Extract data from relational databases',
                  function: 'Query structured data with SQL',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['relational', 'sql', 'batch', 'structured'],
                  tags: ['sql', 'query', 'relational', 'batch'],
                  icon: '🔍',
                  description: 'SQL queries for data extraction',
                  realWorldExample: 'SELECT * FROM customers WHERE...'
                },
                {
                  id: 'comp3.2.2',
                  name: 'ETL Pipeline (Airflow)',
                  purpose: 'Workflow orchestration',
                  function: 'Schedule and monitor batch jobs',
                  cost: { low: 0, high: 2000, unit: 'per month' },
                  compatibility: ['batch', 'etl', 'orchestration', 'scheduling'],
                  tags: ['airflow', 'etl', 'batch', 'orchestration'],
                  icon: '📅',
                  description: 'Apache Airflow workflow scheduler',
                  realWorldExample: 'Airbnb, Lyft use Airflow'
                },
                {
                  id: 'comp3.2.3',
                  name: 'ETL Pipeline (Dagster)',
                  purpose: 'Modern data orchestration',
                  function: 'Build and monitor data pipelines',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['batch', 'etl', 'orchestration', 'modern'],
                  tags: ['dagster', 'etl', 'orchestration', 'data'],
                  icon: '📅',
                  description: 'Dagster data orchestration platform',
                  realWorldExample: 'Used by modern data teams'
                },
                {
                  id: 'comp3.2.4',
                  name: 'SFTP Download',
                  purpose: 'Secure file transfer',
                  function: 'Transfer files securely between systems',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['legacy', 'file-transfer', 'batch', 'secure'],
                  tags: ['sftp', 'file', 'transfer', 'secure'],
                  icon: '📁',
                  description: 'Secure FTP for file transfer',
                  realWorldExample: 'WinSCP, FileZilla Server'
                },
                {
                  id: 'comp3.2.5',
                  name: 'Web Scraper (Scrapy)',
                  purpose: 'Extract web data',
                  function: 'Parse HTML and extract structured data',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['web', 'data-collection', 'scraping', 'python'],
                  tags: ['scrapy', 'scraping', 'web', 'python'],
                  icon: '🕷️',
                  description: 'Scrapy web scraping framework',
                  realWorldExample: 'Used for competitive intelligence'
                },
                {
                  id: 'comp3.2.6',
                  name: 'Web Scraper (BeautifulSoup)',
                  purpose: 'HTML parsing library',
                  function: 'Parse HTML and XML documents',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['web', 'parsing', 'python', 'simple'],
                  tags: ['beautifulsoup', 'parsing', 'web', 'python'],
                  icon: '🕷️',
                  description: 'BeautifulSoup HTML parser',
                  realWorldExample: 'Simple web scraping scripts'
                }
              ]
            },
            {
              id: 'sl3.3',
              name: 'Synthetic Data Engines',
              meaning: 'Generating artificial data via simulation or generative models',
              components: [
                {
                  id: 'comp3.3.1',
                  name: 'Unity/Unreal Simulator',
                  purpose: 'Generate synthetic environments',
                  function: 'Create simulated worlds for training',
                  cost: { low: 0, high: 5000, unit: 'per license' },
                  compatibility: ['simulation', 'gaming', 'robotics', 'vision'],
                  tags: ['unity', 'unreal', 'simulation', '3d'],
                  icon: '🎮',
                  description: 'Game engine for synthetic data generation',
                  realWorldExample: 'Unity ML-Agents, Unreal Engine'
                },
                {
                  id: 'comp3.3.2',
                  name: 'GAN Generator',
                  purpose: 'Generate realistic synthetic data',
                  function: 'Create fake data using adversarial networks',
                  cost: { low: 100, high: 10000, unit: 'per model' },
                  compatibility: ['synthetic', 'gan', 'generation', 'images'],
                  tags: ['gan', 'synthetic', 'generation', 'deep-learning'],
                  icon: '🎨',
                  description: 'Generative Adversarial Network',
                  realWorldExample: 'StyleGAN for face generation'
                },
                {
                  id: 'comp3.3.3',
                  name: 'Diffusion Model',
                  purpose: 'High-quality data generation',
                  function: 'Generate data through iterative denoising',
                  cost: { low: 100, high: 10000, unit: 'per model' },
                  compatibility: ['synthetic', 'diffusion', 'generation', 'images'],
                  tags: ['diffusion', 'synthetic', 'generation'],
                  icon: '🎨',
                  description: 'Diffusion model for data generation',
                  realWorldExample: 'Stable Diffusion, DALL-E'
                },
                {
                  id: 'comp3.3.4',
                  name: 'Procedural Generator',
                  purpose: 'Rule-based data generation',
                  function: 'Generate data using algorithmic rules',
                  cost: { low: 0, high: 1000, unit: 'per generator' },
                  compatibility: ['synthetic', 'procedural', 'rules', 'scalable'],
                  tags: ['procedural', 'synthetic', 'rules', 'algorithmic'],
                  icon: '🔧',
                  description: 'Procedural data generation',
                  realWorldExample: 'Procedural textures, terrain generation'
                },
                {
                  id: 'comp3.3.5',
                  name: 'Mock API',
                  purpose: 'Simulate API responses',
                  function: 'Generate fake API data for testing',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['testing', 'development', 'api', 'mock'],
                  tags: ['mock', 'api', 'testing', 'development'],
                  icon: '🔌',
                  description: 'Mock API for development and testing',
                  realWorldExample: 'JSON Server, MockServiceWorker'
                }
              ]
            },
            {
              id: 'sl3.4',
              name: 'Human-in-the-Loop Acquisition',
              meaning: 'Data generated or labeled directly by human operators',
              components: [
                {
                  id: 'comp3.4.1',
                  name: 'Labeling Tool (Labelbox)',
                  purpose: 'Annotate training data',
                  function: 'Provide UI for humans to label data',
                  cost: { low: 100, high: 10000, unit: 'per month' },
                  compatibility: ['supervised-learning', 'data-labeling', 'quality'],
                  tags: ['labelbox', 'labeling', 'annotation', 'human'],
                  icon: '🏷️',
                  description: 'Labelbox data annotation platform',
                  realWorldExample: 'Used by ML teams for labeling'
                },
                {
                  id: 'comp3.4.2',
                  name: 'Labeling Tool (Scale AI)',
                  purpose: 'Enterprise data annotation',
                  function: 'Provide managed data labeling services',
                  cost: { low: 1000, high: 50000, unit: 'per month' },
                  compatibility: ['enterprise', 'data-labeling', 'managed'],
                  tags: ['scale-ai', 'labeling', 'enterprise', 'managed'],
                  icon: '🏷️',
                  description: 'Scale AI data annotation platform',
                  realWorldExample: 'Used by OpenAI, Meta'
                },
                {
                  id: 'comp3.4.3',
                  name: 'Crowdsourcing (Mechanical Turk)',
                  purpose: 'Distributed human intelligence',
                  function: 'Outsource labeling to crowd workers',
                  cost: { low: 0.01, high: 1, unit: 'per task' },
                  compatibility: ['large-scale', 'simple-tasks', 'cost-effective'],
                  tags: ['crowd', 'mturk', 'distributed', 'cheap'],
                  icon: '👥',
                  description: 'Amazon Mechanical Turk',
                  realWorldExample: 'Used for large-scale labeling'
                },
                {
                  id: 'comp3.4.4',
                  name: 'Gamification',
                  purpose: 'Engage users in data generation',
                  function: 'Generate data through game mechanics',
                  cost: { low: 100, high: 10000, unit: 'per project' },
                  compatibility: ['crowdsourcing', 'engagement', 'creative'],
                  tags: ['gamification', 'engagement', 'crowdsourcing'],
                  icon: '🎮',
                  description: 'Game-based data collection',
                  realWorldExample: 'Foldit, Galaxy Zoo'
                },
                {
                  id: 'comp3.4.5',
                  name: 'Expert Annotation',
                  purpose: 'High-quality domain-specific labeling',
                  function: 'Use domain experts for accurate labeling',
                  cost: { low: 100, high: 10000, unit: 'per project' },
                  compatibility: ['medical', 'legal', 'domain-specific', 'high-quality'],
                  tags: ['expert', 'annotation', 'domain', 'quality'],
                  icon: '👨‍⚕️',
                  description: 'Expert annotation for specialized domains',
                  realWorldExample: 'Medical image labeling by doctors'
                }
              ]
            }
          ]
        },
        {
          id: 'layer4',
          name: 'Data Structuring, Storage & Memory',
          keyQuestion: 'How data is organized, persisted, and retrieved',
          subLayers: [
            {
              id: 'sl4.1',
              name: 'Relational Stores',
              meaning: 'Structured data with strict schemas and ACID compliance',
              components: [
                {
                  id: 'comp4.1.1',
                  name: 'PostgreSQL',
                  purpose: 'Advanced relational database',
                  function: 'Store structured data with complex queries',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['structured', 'transactional', 'analytical', 'sql'],
                  tags: ['postgresql', 'sql', 'relational', 'open-source'],
                  icon: '🐘',
                  description: 'Advanced open-source relational database',
                  realWorldExample: 'Instagram, Spotify use PostgreSQL'
                },
                {
                  id: 'comp4.1.2',
                  name: 'MySQL',
                  purpose: 'Popular relational database',
                  function: 'Store structured data for web applications',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['web', 'cms', 'structured', 'sql'],
                  tags: ['mysql', 'sql', 'web', 'popular'],
                  icon: '🐬',
                  description: 'Widely-used open-source relational database',
                  realWorldExample: 'Facebook, Twitter use MySQL'
                },
                {
                  id: 'comp4.1.3',
                  name: 'SQL Server',
                  purpose: 'Microsoft relational database',
                  function: 'Enterprise-grade data storage',
                  cost: { low: 1000, high: 10000, unit: 'per license' },
                  compatibility: ['microsoft', 'enterprise', 'sql', 'business'],
                  tags: ['sql-server', 'microsoft', 'enterprise', 'sql'],
                  icon: '🪟',
                  description: 'Microsoft SQL Server',
                  realWorldExample: 'Used in enterprise Microsoft environments'
                },
                {
                  id: 'comp4.1.4',
                  name: 'SQLite',
                  purpose: 'Embedded relational database',
                  function: 'Store data in a single file',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['embedded', 'mobile', 'lightweight', 'local'],
                  tags: ['sqlite', 'embedded', 'lightweight', 'file-based'],
                  icon: '📱',
                  description: 'Serverless SQL database engine',
                  realWorldExample: 'Used in mobile apps and browsers'
                },
                {
                  id: 'comp4.1.5',
                  name: 'Oracle',
                  purpose: 'Enterprise relational database',
                  function: 'High-performance data management',
                  cost: { low: 5000, high: 50000, unit: 'per license' },
                  compatibility: ['enterprise', 'large-scale', 'sql', 'business'],
                  tags: ['oracle', 'enterprise', 'sql', 'commercial'],
                  icon: '🏢',
                  description: 'Oracle Database',
                  realWorldExample: 'Used in large enterprises'
                }
              ]
            },
            {
              id: 'sl4.2',
              name: 'Document & Object Stores',
              meaning: 'Semi-structured data (JSON/XML) or binary blobs',
              components: [
                {
                  id: 'comp4.2.1',
                  name: 'MongoDB',
                  purpose: 'Document-oriented database',
                  function: 'Store JSON-like documents',
                  cost: { low: 0, high: 2000, unit: 'per month' },
                  compatibility: ['semi-structured', 'flexible', 'web', 'nosql'],
                  tags: ['mongodb', 'nosql', 'document', 'flexible'],
                  icon: '🍃',
                  description: 'Popular document database',
                  realWorldExample: 'eBay, Adobe use MongoDB'
                },
                {
                  id: 'comp4.2.2',
                  name: 'AWS S3',
                  purpose: 'Object storage',
                  function: 'Store and retrieve any amount of data',
                  cost: { low: 0.023, high: 0.09, unit: 'per GB/month' },
                  compatibility: ['blob', 'data-lake', 'backup', 'cloud'],
                  tags: ['s3', 'object', 'cloud', 'scalable'],
                  icon: '🪣',
                  description: 'Scalable cloud object storage',
                  realWorldExample: 'Netflix, Airbnb use S3'
                },
                {
                  id: 'comp4.2.3',
                  name: 'Azure Blob Storage',
                  purpose: 'Microsoft object storage',
                  function: 'Store unstructured data in Azure',
                  cost: { low: 0.018, high: 0.08, unit: 'per GB/month' },
                  compatibility: ['azure', 'blob', 'cloud', 'unstructured'],
                  tags: ['azure', 'blob', 'cloud', 'microsoft'],
                  icon: '☁️',
                  description: 'Azure Blob Storage',
                  realWorldExample: 'Used in Azure cloud environments'
                },
                {
                  id: 'comp4.2.4',
                  name: 'Couchbase',
                  purpose: 'NoSQL document database',
                  function: 'Store and query JSON documents',
                  cost: { low: 0, high: 2000, unit: 'per month' },
                  compatibility: ['nosql', 'document', 'mobile', 'flexible'],
                  tags: ['couchbase', 'nosql', 'document', 'mobile'],
                  icon: '📄',
                  description: 'Couchbase NoSQL database',
                  realWorldExample: 'Used for mobile and web apps'
                },
                {
                  id: 'comp4.2.5',
                  name: 'Firebase',
                  purpose: 'Google mobile backend',
                  function: 'Real-time database for mobile apps',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['mobile', 'real-time', 'google', 'baas'],
                  tags: ['firebase', 'google', 'mobile', 'realtime'],
                  icon: '🔥',
                  description: 'Firebase real-time database',
                  realWorldExample: 'Used in mobile app development'
                }
              ]
            },
            {
              id: 'sl4.3',
              name: 'Vector & Embedding Stores',
              meaning: 'High-dimensional mathematical vectors for similarity search',
              components: [
                {
                  id: 'comp4.3.1',
                  name: 'Pinecone',
                  purpose: 'Managed vector database',
                  function: 'Store and query embeddings at scale',
                  cost: { low: 0, high: 5000, unit: 'per month' },
                  compatibility: ['rag', 'semantic-search', 'llm', 'vector'],
                  tags: ['pinecone', 'vector', 'semantic', 'managed'],
                  icon: '🎯',
                  description: 'Fully managed vector database',
                  realWorldExample: 'Notion, Gong use Pinecone'
                },
                {
                  id: 'comp4.3.2',
                  name: 'Weaviate',
                  purpose: 'Open-source vector database',
                  function: 'Store and search vector embeddings',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['rag', 'semantic-search', 'vector', 'open-source'],
                  tags: ['weaviate', 'vector', 'open-source', 'semantic'],
                  icon: '🔍',
                  description: 'Open-source vector search engine',
                  realWorldExample: 'Used for semantic search applications'
                },
                {
                  id: 'comp4.3.3',
                  name: 'Milvus',
                  purpose: 'Distributed vector database',
                  function: 'High-performance vector similarity search',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['rag', 'vector', 'distributed', 'high-performance'],
                  tags: ['milvus', 'vector', 'distributed', 'performance'],
                  icon: '🚀',
                  description: 'Distributed vector database',
                  realWorldExample: 'Used for large-scale similarity search'
                },
                {
                  id: 'comp4.3.4',
                  name: 'pgvector',
                  purpose: 'PostgreSQL vector extension',
                  function: 'Add vector similarity to PostgreSQL',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['postgres', 'hybrid', 'sql+vector', 'extension'],
                  tags: ['pgvector', 'postgres', 'extension', 'hybrid'],
                  icon: '🔗',
                  description: 'Open-source vector similarity search',
                  realWorldExample: 'Supabase, Neon use pgvector'
                },
                {
                  id: 'comp4.3.5',
                  name: 'FAISS',
                  purpose: 'Facebook AI Similarity Search',
                  function: 'Efficient similarity search library',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['vector', 'similarity', 'library', 'facebook'],
                  tags: ['faiss', 'vector', 'library', 'similarity'],
                  icon: '📚',
                  description: 'FAISS similarity search library',
                  realWorldExample: 'Used by Facebook for similarity search'
                },
                {
                  id: 'comp4.3.6',
                  name: 'Chroma',
                  purpose: 'Embedding database for LLMs',
                  function: 'Store embeddings for RAG applications',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['rag', 'llm', 'embeddings', 'vector'],
                  tags: ['chroma', 'vector', 'embeddings', 'rag'],
                  icon: '🎨',
                  description: 'Chroma vector database',
                  realWorldExample: 'Used in LLM applications'
                }
              ]
            },
            {
              id: 'sl4.4',
              name: 'Graph & Relationship Stores',
              meaning: 'Data optimized for traversing deep relationships',
              components: [
                {
                  id: 'comp4.4.1',
                  name: 'Neo4j',
                  purpose: 'Graph database',
                  function: 'Store and query complex relationships',
                  cost: { low: 0, high: 3000, unit: 'per month' },
                  compatibility: ['knowledge-graph', 'recommendation', 'fraud', 'graph'],
                  tags: ['neo4j', 'graph', 'relationships', 'cypher'],
                  icon: '🕸️',
                  description: 'Leading graph database',
                  realWorldExample: 'Walmart, eBay use Neo4j'
                },
                {
                  id: 'comp4.4.2',
                  name: 'Amazon Neptune',
                  purpose: 'Managed graph database',
                  function: 'Store and query graph data on AWS',
                  cost: { low: 300, high: 5000, unit: 'per month' },
                  compatibility: ['aws', 'graph', 'managed', 'cloud'],
                  tags: ['neptune', 'aws', 'graph', 'managed'],
                  icon: '☁️',
                  description: 'Amazon Neptune graph database',
                  realWorldExample: 'Used for knowledge graphs on AWS'
                },
                {
                  id: 'comp4.4.3',
                  name: 'TigerGraph',
                  purpose: 'High-performance graph database',
                  function: 'Real-time graph analytics',
                  cost: { low: 1000, high: 10000, unit: 'per month' },
                  compatibility: ['graph', 'analytics', 'real-time', 'enterprise'],
                  tags: ['tigergraph', 'graph', 'analytics', 'enterprise'],
                  icon: '🐯',
                  description: 'TigerGraph graph database',
                  realWorldExample: 'Used for fraud detection, recommendation'
                },
                {
                  id: 'comp4.4.4',
                  name: 'ArangoDB',
                  purpose: 'Multi-model database',
                  function: 'Store documents, graphs, and key-values',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['graph', 'document', 'multi-model', 'flexible'],
                  tags: ['arangodb', 'multi-model', 'graph', 'document'],
                  icon: '🔷',
                  description: 'ArangoDB multi-model database',
                  realWorldExample: 'Used for flexible data modeling'
                }
              ]
            },
            {
              id: 'sl4.5',
              name: 'Time-Series Stores',
              meaning: 'Data indexed by timestamps for ranges and aggregations',
              components: [
                {
                  id: 'comp4.5.1',
                  name: 'InfluxDB',
                  purpose: 'Time-series database',
                  function: 'Store and query time-stamped data',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['iot', 'monitoring', 'metrics', 'time-series'],
                  tags: ['influxdb', 'time-series', 'iot', 'metrics'],
                  icon: '📈',
                  description: 'Time-series database for IoT and monitoring',
                  realWorldExample: 'Tesla, Cisco use InfluxDB'
                },
                {
                  id: 'comp4.5.2',
                  name: 'TimescaleDB',
                  purpose: 'PostgreSQL time-series extension',
                  function: 'Time-series data in PostgreSQL',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['postgres', 'time-series', 'sql', 'extension'],
                  tags: ['timescaledb', 'postgres', 'time-series', 'sql'],
                  icon: '⏱️',
                  description: 'TimescaleDB time-series extension',
                  realWorldExample: 'Used for time-series in PostgreSQL'
                },
                {
                  id: 'comp4.5.3',
                  name: 'Prometheus',
                  purpose: 'Metrics database',
                  function: 'Store and query system metrics',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['monitoring', 'metrics', 'time-series', 'alerting'],
                  tags: ['prometheus', 'metrics', 'monitoring', 'time-series'],
                  icon: '📊',
                  description: 'Prometheus metrics database',
                  realWorldExample: 'Used for system monitoring'
                }
              ]
            },
            {
              id: 'sl4.6',
              name: 'Model Artifact Stores',
              meaning: 'Storage for binary weights and biases of trained models',
              components: [
                {
                  id: 'comp4.6.1',
                  name: 'Hugging Face Hub',
                  purpose: 'Model registry',
                  function: 'Store and share pre-trained models',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['nlp', 'transformers', 'pretrained', 'models'],
                  tags: ['huggingface', 'models', 'registry', 'community'],
                  icon: '🤗',
                  description: 'Community model sharing platform',
                  realWorldExample: 'BERT, GPT models on Hugging Face'
                },
                {
                  id: 'comp4.6.2',
                  name: 'MLflow Registry',
                  purpose: 'Model versioning and registry',
                  function: 'Track and manage model versions',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['mlops', 'versioning', 'registry', 'tracking'],
                  tags: ['mlflow', 'registry', 'versioning', 'mlops'],
                  icon: '📦',
                  description: 'MLflow model registry',
                  realWorldExample: 'Used for ML model management'
                },
                {
                  id: 'comp4.6.3',
                  name: 'S3 Bucket (.safetensors)',
                  purpose: 'Cloud model storage',
                  function: 'Store model weights in S3',
                  cost: { low: 0.023, high: 0.09, unit: 'per GB/month' },
                  compatibility: ['aws', 'models', 'cloud', 'storage'],
                  tags: ['s3', 'models', 'storage', 'aws'],
                  icon: '🪣',
                  description: 'S3 storage for model artifacts',
                  realWorldExample: 'Storing PyTorch models in S3'
                },
                {
                  id: 'comp4.6.4',
                  name: 'ONNX Model Storage',
                  purpose: 'Cross-platform model storage',
                  function: 'Store models in ONNX format',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['onnx', 'cross-platform', 'models', 'interchange'],
                  tags: ['onnx', 'models', 'cross-platform', 'storage'],
                  icon: '🔄',
                  description: 'ONNX model format storage',
                  realWorldExample: 'Storing interoperable model formats'
                },
                {
                  id: 'comp4.6.5',
                  name: 'Pickle File (.pkl)',
                  purpose: 'Python object serialization',
                  function: 'Store Python objects and models',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'serialization', 'models', 'legacy'],
                  tags: ['pickle', 'python', 'serialization', 'models'],
                  icon: '🥒',
                  description: 'Pickle file for Python model storage',
                  realWorldExample: 'Storing Scikit-learn models'
                }
              ]
            },
            {
              id: 'sl4.7',
              name: 'Feature & Pre-computation Stores',
              meaning: 'Storage for transformed data ready for ML',
              components: [
                {
                  id: 'comp4.7.1',
                  name: 'Feature Store (Feast)',
                  purpose: 'Feature management',
                  function: 'Manage and serve ML features',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['mlops', 'features', 'consistency', 'serving'],
                  tags: ['feast', 'feature-store', 'mlops', 'features'],
                  icon: '🍽️',
                  description: 'Feast feature store',
                  realWorldExample: 'Gojek, Shopify use Feast'
                },
                {
                  id: 'comp4.7.2',
                  name: 'Feature Store (Tecton)',
                  purpose: 'Enterprise feature platform',
                  function: 'Manage features at enterprise scale',
                  cost: { low: 1000, high: 10000, unit: 'per month' },
                  compatibility: ['enterprise', 'features', 'mlops', 'managed'],
                  tags: ['tecton', 'feature-store', 'enterprise', 'managed'],
                  icon: '🏗️',
                  description: 'Tecton enterprise feature store',
                  realWorldExample: 'Used by enterprise ML teams'
                },
                {
                  id: 'comp4.7.3',
                  name: 'Redis',
                  purpose: 'In-memory data store',
                  function: 'Cache features for real-time inference',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['caching', 'real-time', 'features', 'in-memory'],
                  tags: ['redis', 'cache', 'in-memory', 'fast'],
                  icon: '⚡',
                  description: 'In-memory data structure store',
                  realWorldExample: 'Twitter, GitHub use Redis'
                },
                {
                  id: 'comp4.7.4',
                  name: 'Memcached',
                  purpose: 'Distributed memory caching',
                  function: 'Cache data for fast access',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['caching', 'distributed', 'in-memory', 'fast'],
                  tags: ['memcached', 'cache', 'distributed', 'in-memory'],
                  icon: '💨',
                  description: 'Distributed memory caching system',
                  realWorldExample: 'Used by Facebook, YouTube'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase3',
      name: 'Transformation Layer',
      description: 'Turning raw data into usable signals (features)',
      icon: '🔄',
      layers: [
        {
          id: 'layer5',
          name: 'Data Processing & Feature Engineering',
          keyQuestion: 'Transforming raw data into mathematical representations',
          subLayers: [
            {
              id: 'sl5.1',
              name: 'Cleansing & Validation',
              meaning: 'Removing noise, handling missing values, and ensuring data integrity',
              components: [
                {
                  id: 'comp5.1.1',
                  name: 'Great Expectations',
                  purpose: 'Data validation framework',
                  function: 'Define and test data quality expectations',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['data-quality', 'validation', 'pipeline', 'testing'],
                  tags: ['great-expectations', 'validation', 'quality', 'testing'],
                  icon: '✅',
                  description: 'Data validation and profiling tool',
                  realWorldExample: 'Vimeo, Heineken use Great Expectations'
                },
                {
                  id: 'comp5.1.2',
                  name: 'Pandera',
                  purpose: 'DataFrame validation',
                  function: 'Validate pandas DataFrames with schemas',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['pandas', 'validation', 'data-quality', 'python'],
                  tags: ['pandera', 'pandas', 'validation', 'python'],
                  icon: '✅',
                  description: 'Statistical data validation for pandas',
                  realWorldExample: 'Used in data science pipelines'
                },
                {
                  id: 'comp5.1.3',
                  name: 'Custom Scripts',
                  purpose: 'Custom data cleaning',
                  function: 'Handle domain-specific data issues',
                  cost: { low: 0, high: 1000, unit: 'per project' },
                  compatibility: ['custom', 'domain-specific', 'flexible'],
                  tags: ['custom', 'scripting', 'cleaning', 'flexible'],
                  icon: '📝',
                  description: 'Custom data cleaning scripts',
                  realWorldExample: 'Python scripts for specific data issues'
                },
                {
                  id: 'comp5.1.4',
                  name: 'Null Imputation',
                  purpose: 'Handle missing values',
                  function: 'Fill or remove missing data points',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['data-cleaning', 'missing-values', 'preprocessing'],
                  tags: ['imputation', 'missing-values', 'cleaning', 'null'],
                  icon: '🔧',
                  description: 'Missing value imputation techniques',
                  realWorldExample: 'Mean/median imputation, KNN imputation'
                },
                {
                  id: 'comp5.1.5',
                  name: 'Outlier Removal',
                  purpose: 'Remove anomalous data points',
                  function: 'Detect and remove statistical outliers',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['data-cleaning', 'statistics', 'preprocessing'],
                  tags: ['outliers', 'removal', 'statistics', 'cleaning'],
                  icon: '🎯',
                  description: 'Outlier detection and removal',
                  realWorldExample: 'Z-score, IQR-based outlier removal'
                }
              ]
            },
            {
              id: 'sl5.2',
              name: 'Transformation & Encoding',
              meaning: 'Converting non-numeric data into model-friendly formats',
              components: [
                {
                  id: 'comp5.2.1',
                  name: 'One-Hot Encoding',
                  purpose: 'Convert categorical to numeric',
                  function: 'Create binary columns for categories',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['categorical', 'tabular', 'classical-ml'],
                  tags: ['one-hot', 'encoding', 'categorical', 'numeric'],
                  icon: '🔢',
                  description: 'Convert categorical variables to binary vectors',
                  realWorldExample: 'Scikit-learn OneHotEncoder'
                },
                {
                  id: 'comp5.2.2',
                  name: 'Tokenization (BPE)',
                  purpose: 'Convert text to tokens',
                  function: 'Break text into subword units',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['nlp', 'transformers', 'llm', 'text'],
                  tags: ['tokenization', 'bpe', 'nlp', 'text'],
                  icon: '🔤',
                  description: 'Byte-Pair Encoding for text tokenization',
                  realWorldExample: 'GPT tokenizer, BERT WordPiece'
                },
                {
                  id: 'comp5.2.3',
                  name: 'Tokenization (WordPiece)',
                  purpose: 'Subword tokenization',
                  function: 'Break text into word pieces',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['nlp', 'bert', 'transformers', 'text'],
                  tags: ['tokenization', 'wordpiece', 'bert', 'nlp'],
                  icon: '🔤',
                  description: 'WordPiece tokenization for BERT',
                  realWorldExample: 'BERT tokenizer'
                },
                {
                  id: 'comp5.2.4',
                  name: 'Embedding Generation',
                  purpose: 'Convert data to vectors',
                  function: 'Generate dense vector representations',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['nlp', 'vector', 'semantic', 'llm'],
                  tags: ['embedding', 'vector', 'semantic', 'nlp'],
                  icon: '🧮',
                  description: 'Generate embeddings from data',
                  realWorldExample: 'Word2Vec, Sentence-BERT'
                },
                {
                  id: 'comp5.2.5',
                  name: 'Normalization',
                  purpose: 'Scale features to standard range',
                  function: 'Transform features to common scale',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['preprocessing', 'scaling', 'tabular'],
                  tags: ['normalization', 'scaling', 'preprocessing'],
                  icon: '📏',
                  description: 'Feature normalization',
                  realWorldExample: 'MinMaxScaler, StandardScaler'
                },
                {
                  id: 'comp5.2.6',
                  name: 'Log Scaling',
                  purpose: 'Handle skewed distributions',
                  function: 'Apply logarithmic transformation',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['preprocessing', 'skewed-data', 'tabular'],
                  tags: ['log', 'scaling', 'skewed', 'transformation'],
                  icon: '📐',
                  description: 'Logarithmic feature scaling',
                  realWorldExample: 'Log transform for skewed data'
                }
              ]
            },
            {
              id: 'sl5.3',
              name: 'Feature Selection',
              meaning: 'Reducing dimensionality to improve speed and prevent overfitting',
              components: [
                {
                  id: 'comp5.3.1',
                  name: 'PCA',
                  purpose: 'Dimensionality reduction',
                  function: 'Project data to lower dimensions',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['high-dimensional', 'visualization', 'compression'],
                  tags: ['pca', 'dimensionality', 'reduction', 'unsupervised'],
                  icon: '📉',
                  description: 'Principal Component Analysis',
                  realWorldExample: 'Scikit-learn PCA'
                },
                {
                  id: 'comp5.3.2',
                  name: 't-SNE',
                  purpose: 'Nonlinear dimensionality reduction',
                  function: 'Visualize high-dimensional data',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['visualization', 'exploration', 'nonlinear'],
                  tags: ['t-sne', 'visualization', 'nonlinear', 'reduction'],
                  icon: '📊',
                  description: 't-Distributed Stochastic Neighbor Embedding',
                  realWorldExample: 'Used for data visualization'
                },
                {
                  id: 'comp5.3.3',
                  name: 'Mutual Information',
                  purpose: 'Feature relevance measurement',
                  function: 'Measure dependency between features',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['feature-selection', 'statistics', 'tabular'],
                  tags: ['mutual-information', 'feature-selection', 'statistics'],
                  icon: '📊',
                  description: 'Mutual information for feature selection',
                  realWorldExample: 'Scikit-learn mutual_info_classif'
                },
                {
                  id: 'comp5.3.4',
                  name: 'Recursive Feature Elimination',
                  purpose: 'Iterative feature selection',
                  function: 'Remove weakest features iteratively',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['feature-selection', 'tabular', 'iterative'],
                  tags: ['rfe', 'feature-selection', 'iterative'],
                  icon: '🔁',
                  description: 'Recursive Feature Elimination',
                  realWorldExample: 'Scikit-learn RFE'
                },
                {
                  id: 'comp5.3.5',
                  name: 'Lasso Regularization',
                  purpose: 'Sparse feature selection',
                  function: 'Shrink coefficients to zero',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['feature-selection', 'regularization', 'sparse'],
                  tags: ['lasso', 'regularization', 'sparse', 'feature-selection'],
                  icon: '🎯',
                  description: 'L1 regularization for feature selection',
                  realWorldExample: 'Lasso regression'
                }
              ]
            },
            {
              id: 'sl5.4',
              name: 'Augmentation',
              meaning: 'Artificially expanding dataset to improve generalization',
              components: [
                {
                  id: 'comp5.4.1',
                  name: 'Image Rotation/Cropping',
                  purpose: 'Increase image data variety',
                  function: 'Apply geometric transforms to images',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['computer-vision', 'training', 'regularization'],
                  tags: ['augmentation', 'vision', 'rotation', 'cropping'],
                  icon: '🖼️',
                  description: 'Geometric image augmentation',
                  realWorldExample: 'Random rotation, random crop'
                },
                {
                  id: 'comp5.4.2',
                  name: 'SMOTE',
                  purpose: 'Handle imbalanced datasets',
                  function: 'Synthesize minority class samples',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['imbalanced-data', 'tabular', 'classification'],
                  tags: ['smote', 'imbalanced', 'synthetic', 'oversampling'],
                  icon: '⚖️',
                  description: 'Synthetic Minority Oversampling Technique',
                  realWorldExample: 'imbalanced-learn SMOTE'
                },
                {
                  id: 'comp5.4.3',
                  name: 'Text Back-translation',
                  purpose: 'Text data augmentation',
                  function: 'Translate text to another language and back',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['nlp', 'text', 'augmentation', 'multilingual'],
                  tags: ['back-translation', 'text', 'augmentation', 'nlp'],
                  icon: '🔄',
                  description: 'Back-translation for text augmentation',
                  realWorldExample: 'English→French→English augmentation'
                },
                {
                  id: 'comp5.4.4',
                  name: 'Audio Noise Injection',
                  purpose: 'Audio data augmentation',
                  function: 'Add noise to audio samples',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['audio', 'speech', 'augmentation', 'robustness'],
                  tags: ['audio', 'noise', 'augmentation', 'speech'],
                  icon: '🎵',
                  description: 'Audio noise injection for robustness',
                  realWorldExample: 'Adding background noise to speech'
                }
              ]
            },
            {
              id: 'sl5.5',
              name: 'Context Engineering',
              meaning: 'Structuring prompts or contexts for foundation models/LLMs',
              components: [
                {
                  id: 'comp5.5.1',
                  name: 'Prompt Templates',
                  purpose: 'Structured LLM inputs',
                  function: 'Create reusable prompt patterns',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['llm', 'rag', 'chatbot', 'prompting'],
                  tags: ['prompt', 'template', 'llm', 'structured'],
                  icon: '📝',
                  description: 'Reusable prompt pattern library',
                  realWorldExample: 'LangChain PromptTemplate'
                },
                {
                  id: 'comp5.5.2',
                  name: 'RAG Pipelines',
                  purpose: 'Ground LLM with external knowledge',
                  function: 'Retrieve relevant context for LLM',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['rag', 'llm', 'knowledge', 'retrieval'],
                  tags: ['rag', 'retrieval', 'llm', 'knowledge'],
                  icon: '🔍',
                  description: 'Retrieval-Augmented Generation pipeline',
                  realWorldExample: 'LangChain RAG, LlamaIndex'
                },
                {
                  id: 'comp5.5.3',
                  name: 'Metadata Filtering',
                  purpose: 'Filter relevant context',
                  function: 'Filter documents by metadata',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['rag', 'filtering', 'metadata', 'retrieval'],
                  tags: ['metadata', 'filtering', 'rag', 'retrieval'],
                  icon: '🔖',
                  description: 'Metadata-based context filtering',
                  realWorldExample: 'Filter by date, author, category'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase4',
      name: 'Cognitive Layer',
      description: 'The intelligence core - model architecture and objectives',
      icon: '🧠',
      layers: [
        {
          id: 'layer6',
          name: 'Model Architecture & Learning Paradigm',
          keyQuestion: 'The mathematical blueprint of intelligence',
          subLayers: [
            {
              id: 'sl6.1',
              name: 'Symbolic / Rule-Based',
              meaning: 'Logic explicitly defined by human experts',
              components: [
                {
                  id: 'comp6.1.1',
                  name: 'Expert System',
                  purpose: 'Encode domain expertise',
                  function: 'Apply rules to make decisions',
                  cost: { low: 1000, high: 100000, unit: 'per system' },
                  compatibility: ['rules', 'domain-expertise', 'explainable'],
                  tags: ['expert-system', 'rules', 'symbolic', 'explainable'],
                  icon: '📚',
                  description: 'Rule-based decision system',
                  realWorldExample: 'MYCIN, XCON'
                },
                {
                  id: 'comp6.1.2',
                  name: 'Decision Tree',
                  purpose: 'Interpretable classification',
                  function: 'Split data based on feature thresholds',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['tabular', 'explainable', 'classification'],
                  tags: ['decision-tree', 'interpretable', 'classification'],
                  icon: '🌳',
                  description: 'Tree-based model for decisions',
                  realWorldExample: 'Scikit-learn DecisionTreeClassifier'
                },
                {
                  id: 'comp6.1.3',
                  name: 'Rule Engine (Drools)',
                  purpose: 'Business rule management',
                  function: 'Execute business rules efficiently',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['business-rules', 'enterprise', 'java'],
                  tags: ['drools', 'rules', 'business', 'java'],
                  icon: '⚙️',
                  description: 'Drools business rule engine',
                  realWorldExample: 'Used in enterprise applications'
                },
                {
                  id: 'comp6.1.4',
                  name: 'State Machine',
                  purpose: 'Model state transitions',
                  function: 'Define states and transitions',
                  cost: { low: 0, high: 1000, unit: 'per system' },
                  compatibility: ['control', 'automation', 'workflow'],
                  tags: ['state-machine', 'control', 'automation'],
                  icon: '🔀',
                  description: 'Finite state machine',
                  realWorldExample: 'Traffic light controller, vending machine'
                },
                {
                  id: 'comp6.1.5',
                  name: 'Ontology',
                  purpose: 'Formal knowledge representation',
                  function: 'Define concepts and relationships',
                  cost: { low: 0, high: 10000, unit: 'per system' },
                  compatibility: ['knowledge-graph', 'semantic', 'reasoning'],
                  tags: ['ontology', 'semantic', 'knowledge', 'reasoning'],
                  icon: '📖',
                  description: 'Formal ontology for knowledge representation',
                  realWorldExample: 'Gene Ontology, SNOMED CT'
                }
              ]
            },
            {
              id: 'sl6.2',
              name: 'Classical Statistical ML',
              meaning: 'Algorithms that learn patterns from tabular data',
              components: [
                {
                  id: 'comp6.2.1',
                  name: 'Linear Regression',
                  purpose: 'Predict continuous values',
                  function: 'Fit linear relationship between variables',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['regression', 'simple', 'baseline'],
                  tags: ['linear-regression', 'regression', 'baseline'],
                  icon: '📈',
                  description: 'Linear relationship modeling',
                  realWorldExample: 'House price prediction'
                },
                {
                  id: 'comp6.2.2',
                  name: 'Logistic Regression',
                  purpose: 'Binary classification',
                  function: 'Predict probability of class membership',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classification', 'binary', 'interpretable'],
                  tags: ['logistic-regression', 'classification', 'binary'],
                  icon: '📊',
                  description: 'Logistic regression for classification',
                  realWorldExample: 'Spam detection, credit scoring'
                },
                {
                  id: 'comp6.2.3',
                  name: 'SVM',
                  purpose: 'Maximum margin classification',
                  function: 'Find optimal hyperplane for separation',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classification', 'high-dimensional', 'kernel'],
                  tags: ['svm', 'classification', 'kernel', 'margin'],
                  icon: '✂️',
                  description: 'Support Vector Machine',
                  realWorldExample: 'Text classification, image recognition'
                },
                {
                  id: 'comp6.2.4',
                  name: 'K-Nearest Neighbors',
                  purpose: 'Instance-based learning',
                  function: 'Classify based on nearest neighbors',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classification', 'simple', 'instance-based'],
                  tags: ['knn', 'classification', 'instance-based'],
                  icon: '👥',
                  description: 'K-Nearest Neighbors algorithm',
                  realWorldExample: 'Recommendation systems'
                },
                {
                  id: 'comp6.2.5',
                  name: 'Random Forest',
                  purpose: 'Ensemble classification/regression',
                  function: 'Combine multiple decision trees',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['tabular', 'ensemble', 'robust'],
                  tags: ['random-forest', 'ensemble', 'trees', 'robust'],
                  icon: '🌲',
                  description: 'Ensemble of decision trees',
                  realWorldExample: 'Credit scoring, medical diagnosis'
                },
                {
                  id: 'comp6.2.6',
                  name: 'XGBoost',
                  purpose: 'Gradient boosting',
                  function: 'Sequentially improve weak learners',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['tabular', 'competition', 'high-performance'],
                  tags: ['xgboost', 'boosting', 'gradient', 'performance'],
                  icon: '🚀',
                  description: 'Extreme Gradient Boosting',
                  realWorldExample: 'Kaggle competition winner'
                },
                {
                  id: 'comp6.2.7',
                  name: 'Naive Bayes',
                  purpose: 'Probabilistic classification',
                  function: 'Apply Bayes theorem with independence',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['text', 'classification', 'fast'],
                  tags: ['naive-bayes', 'probabilistic', 'classification'],
                  icon: '🎲',
                  description: 'Naive Bayes classifier',
                  realWorldExample: 'Spam filtering, sentiment analysis'
                }
              ]
            },
            {
              id: 'sl6.3',
              name: 'Deep Neural Networks',
              meaning: 'Multi-layered networks learning hierarchical features',
              components: [
                {
                  id: 'comp6.3.1',
                  name: 'CNN',
                  purpose: 'Image processing',
                  function: 'Extract spatial features hierarchically',
                  cost: { low: 0, high: 1000, unit: 'per training' },
                  compatibility: ['computer-vision', 'images', 'spatial'],
                  tags: ['cnn', 'vision', 'convolution', 'spatial'],
                  icon: '👁️',
                  description: 'Convolutional Neural Network',
                  realWorldExample: 'ResNet, VGG, EfficientNet'
                },
                {
                  id: 'comp6.3.2',
                  name: 'RNN',
                  purpose: 'Sequential data processing',
                  function: 'Process sequences with recurrence',
                  cost: { low: 0, high: 500, unit: 'per training' },
                  compatibility: ['sequence', 'temporal', 'time-series'],
                  tags: ['rnn', 'recurrent', 'sequence', 'temporal'],
                  icon: '🔁',
                  description: 'Recurrent Neural Network',
                  realWorldExample: 'Language modeling, time series'
                },
                {
                  id: 'comp6.3.3',
                  name: 'LSTM',
                  purpose: 'Long-term sequence memory',
                  function: 'Handle long-range dependencies',
                  cost: { low: 0, high: 500, unit: 'per training' },
                  compatibility: ['sequence', 'long-term', 'temporal'],
                  tags: ['lstm', 'sequence', 'memory', 'temporal'],
                  icon: '🔁',
                  description: 'Long Short-Term Memory network',
                  realWorldExample: 'Speech recognition, translation'
                },
                {
                  id: 'comp6.3.4',
                  name: 'Transformer',
                  purpose: 'Sequence processing with attention',
                  function: 'Process sequences with self-attention',
                  cost: { low: 100, high: 1000000, unit: 'per training' },
                  compatibility: ['nlp', 'llm', 'sequence', 'attention'],
                  tags: ['transformer', 'attention', 'sequence', 'foundation'],
                  icon: '🔄',
                  description: 'Attention-based architecture',
                  realWorldExample: 'GPT, BERT, T5, LLaMA'
                },
                {
                  id: 'comp6.3.5',
                  name: 'GAN',
                  purpose: 'Generative modeling',
                  function: 'Generate data via adversarial training',
                  cost: { low: 100, high: 10000, unit: 'per training' },
                  compatibility: ['generation', 'adversarial', 'images'],
                  tags: ['gan', 'generative', 'adversarial', 'images'],
                  icon: '🎨',
                  description: 'Generative Adversarial Network',
                  realWorldExample: 'StyleGAN, CycleGAN'
                },
                {
                  id: 'comp6.3.6',
                  name: 'VAE',
                  purpose: 'Latent space generation',
                  function: 'Learn latent representations',
                  cost: { low: 100, high: 5000, unit: 'per training' },
                  compatibility: ['generation', 'latent', 'compression'],
                  tags: ['vae', 'variational', 'latent', 'generation'],
                  icon: '🧬',
                  description: 'Variational Autoencoder',
                  realWorldExample: 'Image generation, anomaly detection'
                },
                {
                  id: 'comp6.3.7',
                  name: 'Diffusion Model',
                  purpose: 'High-quality generation',
                  function: 'Generate via iterative denoising',
                  cost: { low: 100, high: 50000, unit: 'per training' },
                  compatibility: ['generation', 'images', 'high-quality'],
                  tags: ['diffusion', 'generation', 'denoising'],
                  icon: '🎨',
                  description: 'Diffusion model for generation',
                  realWorldExample: 'Stable Diffusion, DALL-E 2'
                },
                {
                  id: 'comp6.3.8',
                  name: 'Graph Neural Network',
                  purpose: 'Graph-structured learning',
                  function: 'Learn from graph data',
                  cost: { low: 0, high: 1000, unit: 'per training' },
                  compatibility: ['graphs', 'relationships', 'structured'],
                  tags: ['gnn', 'graph', 'structured', 'relationships'],
                  icon: '🕸️',
                  description: 'Graph Neural Network',
                  realWorldExample: 'Social networks, molecular graphs'
                }
              ]
            },
            {
              id: 'sl6.4',
              name: 'Reinforcement Learning',
              meaning: 'Agents learning optimal policies through interaction',
              components: [
                {
                  id: 'comp6.4.1',
                  name: 'DQN',
                  purpose: 'Deep Q-Learning',
                  function: 'Learn action values with neural networks',
                  cost: { low: 100, high: 5000, unit: 'per training' },
                  compatibility: ['games', 'discrete-actions', 'atari'],
                  tags: ['dqn', 'q-learning', 'deep', 'discrete'],
                  icon: '🕹️',
                  description: 'Deep Q-Network',
                  realWorldExample: 'Atari game playing AI'
                },
                {
                  id: 'comp6.4.2',
                  name: 'PPO',
                  purpose: 'Policy optimization',
                  function: 'Optimize policy with clipped updates',
                  cost: { low: 100, high: 10000, unit: 'per training' },
                  compatibility: ['games', 'robotics', 'control', 'continuous'],
                  tags: ['ppo', 'policy', 'optimization', 'control'],
                  icon: '🎮',
                  description: 'Proximal Policy Optimization',
                  realWorldExample: 'OpenAI Five, ChatGPT RLHF'
                },
                {
                  id: 'comp6.4.3',
                  name: 'A3C',
                  purpose: 'Asynchronous RL',
                  function: 'Parallel actor-critic learning',
                  cost: { low: 100, high: 5000, unit: 'per training' },
                  compatibility: ['parallel', 'actor-critic', 'asynchronous'],
                  tags: ['a3c', 'actor-critic', 'asynchronous', 'parallel'],
                  icon: '⚡',
                  description: 'Asynchronous Advantage Actor-Critic',
                  realWorldExample: 'Used in distributed RL systems'
                },
                {
                  id: 'comp6.4.4',
                  name: 'Actor-Critic Methods',
                  purpose: 'Policy and value learning',
                  function: 'Learn policy and value simultaneously',
                  cost: { low: 100, high: 5000, unit: 'per training' },
                  compatibility: ['continuous', 'control', 'robotics'],
                  tags: ['actor-critic', 'policy', 'value', 'control'],
                  icon: '🎭',
                  description: 'Actor-Critic architecture',
                  realWorldExample: 'SAC, TD3 algorithms'
                },
                {
                  id: 'comp6.4.5',
                  name: 'Multi-Armed Bandit',
                  purpose: 'Explore-exploit tradeoff',
                  function: 'Optimize action selection',
                  cost: { low: 0, high: 100, unit: 'per implementation' },
                  compatibility: ['recommendation', 'optimization', 'exploration'],
                  tags: ['bandit', 'exploration', 'optimization', 'simple'],
                  icon: '🎰',
                  description: 'Multi-armed bandit algorithm',
                  realWorldExample: 'A/B testing, recommendation'
                }
              ]
            },
            {
              id: 'sl6.5',
              name: 'Search & Optimization',
              meaning: 'Algorithms that explore state spaces',
              components: [
                {
                  id: 'comp6.5.1',
                  name: 'A* Search',
                  purpose: 'Pathfinding algorithm',
                  function: 'Find shortest path efficiently',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['pathfinding', 'navigation', 'planning'],
                  tags: ['a-star', 'pathfinding', 'navigation', 'search'],
                  icon: '🗺️',
                  description: 'A* search algorithm',
                  realWorldExample: 'GPS navigation, game AI'
                },
                {
                  id: 'comp6.5.2',
                  name: 'Monte Carlo Tree Search',
                  purpose: 'Decision making in games',
                  function: 'Explore game tree with random sampling',
                  cost: { low: 0, high: 100, unit: 'per search' },
                  compatibility: ['games', 'planning', 'search', 'decision'],
                  tags: ['mcts', 'search', 'planning', 'games'],
                  icon: '🎲',
                  description: 'Monte Carlo Tree Search',
                  realWorldExample: 'AlphaGo, AlphaZero'
                },
                {
                  id: 'comp6.5.3',
                  name: 'Genetic Algorithm',
                  purpose: 'Evolutionary optimization',
                  function: 'Optimize via natural selection',
                  cost: { low: 0, high: 100, unit: 'per run' },
                  compatibility: ['optimization', 'evolutionary', 'search'],
                  tags: ['genetic', 'evolutionary', 'optimization'],
                  icon: '🧬',
                  description: 'Genetic algorithm for optimization',
                  realWorldExample: 'Feature selection, hyperparameter tuning'
                },
                {
                  id: 'comp6.5.4',
                  name: 'Simulated Annealing',
                  purpose: 'Global optimization',
                  function: 'Find global optimum by cooling',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['optimization', 'global-search', 'scheduling'],
                  tags: ['simulated-annealing', 'optimization', 'global'],
                  icon: '🌡️',
                  description: 'Simulated annealing algorithm',
                  realWorldExample: 'Scheduling, routing problems'
                },
                {
                  id: 'comp6.5.5',
                  name: 'Particle Swarm',
                  purpose: 'Swarm intelligence optimization',
                  function: 'Optimize via collective behavior',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['optimization', 'continuous', 'swarm'],
                  tags: ['particle-swarm', 'optimization', 'swarm'],
                  icon: '🐦',
                  description: 'Particle Swarm Optimization',
                  realWorldExample: 'Neural network training, engineering'
                }
              ]
            },
            {
              id: 'sl6.6',
              name: 'Ensemble & Meta-Learning',
              meaning: 'Combining multiple models for better performance',
              components: [
                {
                  id: 'comp6.6.1',
                  name: 'Bagging',
                  purpose: 'Reduce variance',
                  function: 'Train models on bootstrap samples',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['ensemble', 'variance-reduction', 'parallel'],
                  tags: ['bagging', 'ensemble', 'bootstrap', 'variance'],
                  icon: '🎒',
                  description: 'Bootstrap Aggregating',
                  realWorldExample: 'Random Forest uses bagging'
                },
                {
                  id: 'comp6.6.2',
                  name: 'Boosting',
                  purpose: 'Reduce bias',
                  function: 'Sequentially improve weak learners',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['ensemble', 'bias-reduction', 'sequential'],
                  tags: ['boosting', 'ensemble', 'sequential', 'bias'],
                  icon: '📈',
                  description: 'Boosting ensemble method',
                  realWorldExample: 'AdaBoost, Gradient Boosting'
                },
                {
                  id: 'comp6.6.3',
                  name: 'Stacking',
                  purpose: 'Combine diverse models',
                  function: 'Train meta-model on base predictions',
                  cost: { low: 0, high: 100, unit: 'per implementation' },
                  compatibility: ['ensemble', 'meta-learning', 'diverse'],
                  tags: ['stacking', 'ensemble', 'meta-learning'],
                  icon: '📚',
                  description: 'Stacked generalization',
                  realWorldExample: 'Winning Kaggle solutions'
                },
                {
                  id: 'comp6.6.4',
                  name: 'Model Cascades',
                  purpose: 'Efficient inference',
                  function: 'Route to simpler models first',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['efficiency', 'routing', 'cost-optimization'],
                  tags: ['cascade', 'routing', 'efficiency'],
                  icon: '🌊',
                  description: 'Model cascade for efficiency',
                  realWorldExample: 'Easy vs hard example routing'
                },
                {
                  id: 'comp6.6.5',
                  name: 'Mixture of Experts (MoE)',
                  purpose: 'Conditional computation',
                  function: 'Route inputs to specialized experts',
                  cost: { low: 1000, high: 1000000, unit: 'per training' },
                  compatibility: ['llm', 'scaling', 'efficiency', 'routing'],
                  tags: ['moe', 'experts', 'routing', 'efficient'],
                  icon: '👥',
                  description: 'Mixture of Experts architecture',
                  realWorldExample: 'GPT-4, Mistral 8x7B'
                },
                {
                  id: 'comp6.6.6',
                  name: 'Few-Shot Learner',
                  purpose: 'Learn from few examples',
                  function: 'Adapt to new tasks with minimal data',
                  cost: { low: 0, high: 100, unit: 'per task' },
                  compatibility: ['llm', 'adaptation', 'nlp'],
                  tags: ['few-shot', 'adaptation', 'llm', 'meta-learning'],
                  icon: '🎯',
                  description: 'Few-shot learning capability',
                  realWorldExample: 'GPT-3 few-shot prompting'
                }
              ]
            }
          ]
        },
        {
          id: 'layer7',
          name: 'Objective, Optimization & Alignment',
          keyQuestion: 'The definition of success and mechanism to achieve it',
          subLayers: [
            {
              id: 'sl7.1',
              name: 'Loss Functions (Error Metrics)',
              meaning: 'Mathematical formulas measuring prediction error',
              components: [
                {
                  id: 'comp7.1.1',
                  name: 'Cross-Entropy',
                  purpose: 'Classification loss',
                  function: 'Measure difference between distributions',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classification', 'nlp', 'vision'],
                  tags: ['cross-entropy', 'loss', 'classification'],
                  icon: '📊',
                  description: 'Standard classification loss',
                  realWorldExample: 'PyTorch CrossEntropyLoss'
                },
                {
                  id: 'comp7.1.2',
                  name: 'MSE',
                  purpose: 'Regression loss',
                  function: 'Measure squared error',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['regression', 'continuous', 'prediction'],
                  tags: ['mse', 'loss', 'regression', 'squared'],
                  icon: '📉',
                  description: 'Mean Squared Error',
                  realWorldExample: 'PyTorch MSELoss'
                },
                {
                  id: 'comp7.1.3',
                  name: 'Huber Loss',
                  purpose: 'Robust regression loss',
                  function: 'Combine MSE and MAE benefits',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['regression', 'outliers', 'robust'],
                  tags: ['huber', 'loss', 'robust', 'regression'],
                  icon: '📈',
                  description: 'Huber loss for robust regression',
                  realWorldExample: 'Used in object detection'
                },
                {
                  id: 'comp7.1.4',
                  name: 'Contrastive Loss',
                  purpose: 'Similarity learning',
                  function: 'Learn embeddings by comparison',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['embeddings', 'similarity', 'metric-learning'],
                  tags: ['contrastive', 'loss', 'similarity', 'embeddings'],
                  icon: '⚖️',
                  description: 'Contrastive loss for similarity learning',
                  realWorldExample: 'Siamese networks, CLIP'
                },
                {
                  id: 'comp7.1.5',
                  name: 'KL Divergence',
                  purpose: 'Distribution comparison',
                  function: 'Measure difference between distributions',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['distribution', 'generative', 'vae'],
                  tags: ['kl-divergence', 'distribution', 'generative'],
                  icon: '📊',
                  description: 'Kullback-Leibler Divergence',
                  realWorldExample: 'VAE training, distillation'
                },
                {
                  id: 'comp7.1.6',
                  name: 'CTC Loss',
                  purpose: 'Sequence alignment loss',
                  function: 'Handle unaligned sequences',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['speech', 'ocr', 'sequence'],
                  tags: ['ctc', 'loss', 'sequence', 'alignment'],
                  icon: '🔤',
                  description: 'Connectionist Temporal Classification loss',
                  realWorldExample: 'Speech recognition, OCR'
                }
              ]
            },
            {
              id: 'sl7.2',
              name: 'Optimizers',
              meaning: 'Algorithms that adjust model weights to minimize loss',
              components: [
                {
                  id: 'comp7.2.1',
                  name: 'SGD',
                  purpose: 'Stochastic gradient descent',
                  function: 'Update weights using gradients',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classical-ml', 'simple', 'generalization'],
                  tags: ['sgd', 'optimizer', 'gradient', 'classic'],
                  icon: '📐',
                  description: 'Stochastic Gradient Descent',
                  realWorldExample: 'Preferred for some vision tasks'
                },
                {
                  id: 'comp7.2.2',
                  name: 'Adam',
                  purpose: 'Adaptive optimization',
                  function: 'Adapt learning rates per parameter',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['deep-learning', 'default', 'efficient'],
                  tags: ['adam', 'optimizer', 'adaptive', 'default'],
                  icon: '⚙️',
                  description: 'Adaptive Moment Estimation',
                  realWorldExample: 'Default optimizer in most frameworks'
                },
                {
                  id: 'comp7.2.3',
                  name: 'RMSprop',
                  purpose: 'Adaptive learning rates',
                  function: 'Normalize gradients by magnitude',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['rnn', 'adaptive', 'non-stationary'],
                  tags: ['rmsprop', 'optimizer', 'adaptive'],
                  icon: '⚙️',
                  description: 'Root Mean Square Propagation',
                  realWorldExample: 'Used in RNN training'
                },
                {
                  id: 'comp7.2.4',
                  name: 'Adagrad',
                  purpose: 'Adaptive per-parameter rates',
                  function: 'Adapt learning rates by frequency',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['sparse', 'adaptive', 'text'],
                  tags: ['adagrad', 'optimizer', 'sparse', 'adaptive'],
                  icon: '⚙️',
                  description: 'Adaptive Gradient Algorithm',
                  realWorldExample: 'Used for sparse features'
                },
                {
                  id: 'comp7.2.5',
                  name: 'L-BFGS',
                  purpose: 'Quasi-Newton optimization',
                  function: 'Approximate second-order methods',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['small-scale', 'batch', 'classical'],
                  tags: ['l-bfgs', 'optimizer', 'quasi-newton'],
                  icon: '📐',
                  description: 'Limited-memory BFGS',
                  realWorldExample: 'Used in classical ML'
                },
                {
                  id: 'comp7.2.6',
                  name: 'Lion',
                  purpose: 'Efficient optimization',
                  function: 'Sign-based optimization',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['large-models', 'efficient', 'modern'],
                  tags: ['lion', 'optimizer', 'efficient', 'modern'],
                  icon: '🦁',
                  description: 'EvoLved Sign Momentum optimizer',
                  realWorldExample: 'Used in modern LLM training'
                }
              ]
            },
            {
              id: 'sl7.3',
              name: 'Reward Modeling & RLHF',
              meaning: 'Training separate models to represent human preferences',
              components: [
                {
                  id: 'comp7.3.1',
                  name: 'Bradley-Terry Model',
                  purpose: 'Preference modeling',
                  function: 'Model pairwise preferences',
                  cost: { low: 0, high: 100, unit: 'per model' },
                  compatibility: ['preference', 'ranking', 'rlhf'],
                  tags: ['bradley-terry', 'preference', 'ranking'],
                  icon: '⚖️',
                  description: 'Bradley-Terry preference model',
                  realWorldExample: 'Used in RLHF reward modeling'
                },
                {
                  id: 'comp7.3.2',
                  name: 'PPO (for RLHF)',
                  purpose: 'Policy optimization for alignment',
                  function: 'Optimize policy with human feedback',
                  cost: { low: 1000, high: 100000, unit: 'per model' },
                  compatibility: ['llm', 'alignment', 'rlhf'],
                  tags: ['ppo', 'rlhf', 'alignment', 'policy'],
                  icon: '🤝',
                  description: 'PPO for Reinforcement Learning from Human Feedback',
                  realWorldExample: 'ChatGPT, InstructGPT training'
                },
                {
                  id: 'comp7.3.3',
                  name: 'DPO',
                  purpose: 'Direct preference optimization',
                  function: 'Optimize directly from preferences',
                  cost: { low: 100, high: 10000, unit: 'per model' },
                  compatibility: ['llm', 'alignment', 'efficient'],
                  tags: ['dpo', 'preference', 'efficient', 'alignment'],
                  icon: '🎯',
                  description: 'Direct Preference Optimization',
                  realWorldExample: 'Mistral, Zephyr models'
                },
                {
                  id: 'comp7.3.4',
                  name: 'Constitutional AI',
                  purpose: 'AI alignment with principles',
                  function: 'Align with constitutional principles',
                  cost: { low: 1000, high: 50000, unit: 'per model' },
                  compatibility: ['llm', 'safety', 'alignment', 'ethics'],
                  tags: ['constitutional-ai', 'safety', 'alignment', 'ethics'],
                  icon: '📜',
                  description: 'Constitutional AI for safe behavior',
                  realWorldExample: 'Anthropic Claude training'
                }
              ]
            },
            {
              id: 'sl7.4',
              name: 'Constraint & Policy Enforcement',
              meaning: 'Hard boundaries preventing certain actions',
              components: [
                {
                  id: 'comp7.4.1',
                  name: 'Action Masking (RL)',
                  purpose: 'Prevent invalid actions',
                  function: 'Mask illegal actions in RL',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['reinforcement-learning', 'constraints', 'safety'],
                  tags: ['action-masking', 'rl', 'constraints', 'safety'],
                  icon: '🎭',
                  description: 'Action masking in reinforcement learning',
                  realWorldExample: 'Preventing illegal moves in games'
                },
                {
                  id: 'comp7.4.2',
                  name: 'Output Validators',
                  purpose: 'Ensure valid outputs',
                  function: 'Check outputs against rules',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['safety', 'compliance', 'production'],
                  tags: ['validation', 'safety', 'output', 'compliance'],
                  icon: '🛡️',
                  description: 'Validate model outputs',
                  realWorldExample: 'Pydantic validators, schema enforcement'
                },
                {
                  id: 'comp7.4.3',
                  name: 'Guardrails',
                  purpose: 'AI safety boundaries',
                  function: 'Prevent harmful outputs',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['llm', 'safety', 'compliance', 'production'],
                  tags: ['guardrails', 'safety', 'llm', 'boundaries'],
                  icon: '🛡️',
                  description: 'Guardrails for AI safety',
                  realWorldExample: 'NVIDIA NeMo Guardrails'
                },
                {
                  id: 'comp7.4.4',
                  name: 'Safety Classifiers',
                  purpose: 'Detect unsafe content',
                  function: 'Classify content safety',
                  cost: { low: 100, high: 1000, unit: 'per month' },
                  compatibility: ['safety', 'content-moderation', 'llm'],
                  tags: ['safety', 'classifier', 'moderation'],
                  icon: '🚦',
                  description: 'Safety classification system',
                  realWorldExample: 'OpenAI content filter'
                }
              ]
            },
            {
              id: 'sl7.5',
              name: 'Hyperparameter Logic',
              meaning: 'Settings governing the training process',
              components: [
                {
                  id: 'comp7.5.1',
                  name: 'Learning Rate Scheduler',
                  purpose: 'Dynamic learning rates',
                  function: 'Adjust LR during training',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['training', 'convergence', 'deep-learning'],
                  tags: ['scheduler', 'learning-rate', 'training'],
                  icon: '📊',
                  description: 'Schedule learning rate changes',
                  realWorldExample: 'Cosine annealing, warmup'
                },
                {
                  id: 'comp7.5.2',
                  name: 'Batch Size Logic',
                  purpose: 'Batch size optimization',
                  function: 'Determine optimal batch size',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['training', 'memory', 'performance'],
                  tags: ['batch-size', 'training', 'memory'],
                  icon: '📦',
                  description: 'Batch size configuration',
                  realWorldExample: 'Dynamic batch sizing'
                },
                {
                  id: 'comp7.5.3',
                  name: 'Dropout Rates',
                  purpose: 'Regularization',
                  function: 'Prevent overfitting',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['regularization', 'training', 'generalization'],
                  tags: ['dropout', 'regularization', 'training'],
                  icon: '🎲',
                  description: 'Dropout regularization',
                  realWorldExample: 'Dropout(0.5) in neural networks'
                },
                {
                  id: 'comp7.5.4',
                  name: 'Early Stopping Criteria',
                  purpose: 'Prevent overfitting',
                  function: 'Stop training at optimal point',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['training', 'validation', 'generalization'],
                  tags: ['early-stopping', 'training', 'validation'],
                  icon: '⏹️',
                  description: 'Early stopping for training',
                  realWorldExample: 'Monitor validation loss'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase5',
      name: 'Execution Layer',
      description: 'Turning abstract math into executable artifacts',
      icon: '⚡',
      layers: [
        {
          id: 'layer8',
          name: 'Compilation, Packaging & Inference Engine',
          keyQuestion: 'Turning abstract math into executable software',
          subLayers: [
            {
              id: 'sl8.1',
              name: 'Model Compilers & Runtimes',
              meaning: 'Translating high-level graphs to optimized low-level code',
              components: [
                {
                  id: 'comp8.1.1',
                  name: 'ONNX Runtime',
                  purpose: 'Cross-platform inference',
                  function: 'Run models across frameworks',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['cross-platform', 'production', 'optimization'],
                  tags: ['onnx', 'runtime', 'cross-platform'],
                  icon: '🔧',
                  description: 'Open Neural Network Exchange runtime',
                  realWorldExample: 'Microsoft, NVIDIA use ONNX'
                },
                {
                  id: 'comp8.1.2',
                  name: 'TensorRT',
                  purpose: 'NVIDIA inference optimization',
                  function: 'Optimize models for NVIDIA GPUs',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['nvidia', 'gpu', 'high-performance'],
                  tags: ['tensorrt', 'nvidia', 'gpu', 'optimization'],
                  icon: '🚀',
                  description: 'NVIDIA inference optimizer',
                  realWorldExample: 'Production deployment on NVIDIA GPUs'
                },
                {
                  id: 'comp8.1.3',
                  name: 'OpenVINO',
                  purpose: 'Intel inference optimization',
                  function: 'Optimize models for Intel hardware',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['intel', 'cpu', 'edge', 'optimization'],
                  tags: ['openvino', 'intel', 'cpu', 'edge'],
                  icon: '🔧',
                  description: 'Intel OpenVINO toolkit',
                  realWorldExample: 'Edge deployment on Intel hardware'
                },
                {
                  id: 'comp8.1.4',
                  name: 'XLA',
                  purpose: 'Accelerated Linear Algebra',
                  function: 'Compile and optimize TensorFlow graphs',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['tensorflow', 'compilation', 'optimization'],
                  tags: ['xla', 'tensorflow', 'compilation'],
                  icon: '⚡',
                  description: 'XLA compiler for TensorFlow',
                  realWorldExample: 'Used in TensorFlow production'
                },
                {
                  id: 'comp8.1.5',
                  name: 'Core ML',
                  purpose: 'Apple model deployment',
                  function: 'Deploy models on Apple devices',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['apple', 'ios', 'macos', 'on-device'],
                  tags: ['core-ml', 'apple', 'ios', 'on-device'],
                  icon: '🍎',
                  description: 'Apple Core ML framework',
                  realWorldExample: 'ML on iPhone, iPad, Mac'
                }
              ]
            },
            {
              id: 'sl8.2',
              name: 'Framework Libraries',
              meaning: 'Software toolkits for model building and inference',
              components: [
                {
                  id: 'comp8.2.1',
                  name: 'PyTorch',
                  purpose: 'Deep learning framework',
                  function: 'Define and train neural networks',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['deep-learning', 'research', 'production'],
                  tags: ['pytorch', 'framework', 'deep-learning', 'python'],
                  icon: '🔥',
                  description: 'Popular deep learning framework',
                  realWorldExample: 'Meta AI, Tesla use PyTorch'
                },
                {
                  id: 'comp8.2.2',
                  name: 'TensorFlow',
                  purpose: 'Production ML framework',
                  function: 'Build and deploy ML models',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['production', 'mobile', 'serving'],
                  tags: ['tensorflow', 'framework', 'production', 'google'],
                  icon: '📦',
                  description: 'Google production ML framework',
                  realWorldExample: 'Google products use TensorFlow'
                },
                {
                  id: 'comp8.2.3',
                  name: 'JAX',
                  purpose: 'High-performance ML',
                  function: 'Autograd and XLA compilation',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['research', 'high-performance', 'functional'],
                  tags: ['jax', 'research', 'functional', 'google'],
                  icon: '⚡',
                  description: 'JAX numerical computing',
                  realWorldExample: 'DeepMind uses JAX'
                },
                {
                  id: 'comp8.2.4',
                  name: 'Scikit-learn',
                  purpose: 'Classical ML library',
                  function: 'Implement classical ML algorithms',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['classical-ml', 'tabular', 'simple'],
                  tags: ['scikit-learn', 'classical-ml', 'python'],
                  icon: '📊',
                  description: 'Classical ML library',
                  realWorldExample: 'Industry standard for classical ML'
                },
                {
                  id: 'comp8.2.5',
                  name: 'Hugging Face Transformers',
                  purpose: 'Transformer model library',
                  function: 'Use pre-trained transformer models',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['transformers', 'nlp', 'pretrained'],
                  tags: ['huggingface', 'transformers', 'nlp'],
                  icon: '🤗',
                  description: 'Transformer model library',
                  realWorldExample: 'BERT, GPT models'
                },
                {
                  id: 'comp8.2.6',
                  name: 'Keras',
                  purpose: 'High-level neural network API',
                  function: 'Build neural networks easily',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['neural-networks', 'beginner', 'tensorflow'],
                  tags: ['keras', 'api', 'neural-networks'],
                  icon: '🧠',
                  description: 'High-level neural network API',
                  realWorldExample: 'Rapid prototyping with Keras'
                }
              ]
            },
            {
              id: 'sl8.3',
              name: 'Optimization & Compression',
              meaning: 'Reducing model size and increasing speed',
              components: [
                {
                  id: 'comp8.3.1',
                  name: 'Quantization (FP16)',
                  purpose: 'Half-precision inference',
                  function: 'Convert FP32 to FP16',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['gpu', 'efficiency', 'inference'],
                  tags: ['quantization', 'fp16', 'efficiency'],
                  icon: '📏',
                  description: 'FP16 quantization',
                  realWorldExample: 'Mixed precision training'
                },
                {
                  id: 'comp8.3.2',
                  name: 'Quantization (INT8)',
                  purpose: 'Integer quantization',
                  function: 'Convert to 8-bit integers',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['edge', 'mobile', 'efficiency'],
                  tags: ['quantization', 'int8', 'edge'],
                  icon: '📏',
                  description: 'INT8 quantization',
                  realWorldExample: 'TensorRT INT8 inference'
                },
                {
                  id: 'comp8.3.3',
                  name: 'Pruning',
                  purpose: 'Remove unnecessary weights',
                  function: 'Sparsify neural networks',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['compression', 'efficiency', 'deployment'],
                  tags: ['pruning', 'sparse', 'efficiency'],
                  icon: '✂️',
                  description: 'Network pruning',
                  realWorldExample: 'PyTorch pruning utilities'
                },
                {
                  id: 'comp8.3.4',
                  name: 'Knowledge Distillation',
                  purpose: 'Model compression',
                  function: 'Train small model from large model',
                  cost: { low: 0, high: 100, unit: 'per model' },
                  compatibility: ['compression', 'efficiency', 'deployment'],
                  tags: ['distillation', 'compression', 'efficiency'],
                  icon: '🎓',
                  description: 'Knowledge distillation',
                  realWorldExample: 'DistilBERT from BERT'
                },
                {
                  id: 'comp8.3.5',
                  name: 'Weight Clustering',
                  purpose: 'Reduce model size',
                  function: 'Cluster similar weights',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['compression', 'efficiency'],
                  tags: ['clustering', 'compression', 'weights'],
                  icon: '🔗',
                  description: 'Weight clustering compression',
                  realWorldExample: 'TensorFlow weight clustering'
                }
              ]
            },
            {
              id: 'sl8.4',
              name: 'Containerization & Env Mgmt',
              meaning: 'Packaging code and dependencies into isolated units',
              components: [
                {
                  id: 'comp8.4.1',
                  name: 'Docker',
                  purpose: 'Containerization',
                  function: 'Package apps with dependencies',
                  cost: { low: 0, high: 21, unit: 'per month' },
                  compatibility: ['deployment', 'reproducibility', 'microservices'],
                  tags: ['docker', 'container', 'deployment'],
                  icon: '🐳',
                  description: 'Container platform',
                  realWorldExample: 'Industry standard for deployment'
                },
                {
                  id: 'comp8.4.2',
                  name: 'Podman',
                  purpose: 'Daemonless containers',
                  function: 'Run containers without daemon',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['containers', 'security', 'daemonless'],
                  tags: ['podman', 'container', 'daemonless'],
                  icon: '📦',
                  description: 'Podman container engine',
                  realWorldExample: 'RHEL container management'
                },
                {
                  id: 'comp8.4.3',
                  name: 'Conda',
                  purpose: 'Package management',
                  function: 'Manage Python environments',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'environments', 'data-science'],
                  tags: ['conda', 'environment', 'python'],
                  icon: '🐍',
                  description: 'Conda package manager',
                  realWorldExample: 'Anaconda, Miniconda'
                },
                {
                  id: 'comp8.4.4',
                  name: 'Virtualenv',
                  purpose: 'Python environment isolation',
                  function: 'Isolate Python dependencies',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'isolation', 'dependencies'],
                  tags: ['virtualenv', 'python', 'isolation'],
                  icon: '🔒',
                  description: 'Python virtual environment',
                  realWorldExample: 'Standard Python environment tool'
                },
                {
                  id: 'comp8.4.5',
                  name: 'PyPI/NPM Packages',
                  purpose: 'Package distribution',
                  function: 'Distribute and install packages',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'javascript', 'packages'],
                  tags: ['pypi', 'npm', 'packages', 'distribution'],
                  icon: '📦',
                  description: 'Package registries',
                  realWorldExample: 'pip install, npm install'
                }
              ]
            },
            {
              id: 'sl8.5',
              name: 'Execution Patterns',
              meaning: 'Specific modes of calling the model',
              components: [
                {
                  id: 'comp8.5.1',
                  name: 'Real-time predict() API',
                  purpose: 'Synchronous inference',
                  function: 'Serve predictions on demand',
                  cost: { low: 10, high: 1000, unit: 'per month' },
                  compatibility: ['web', 'interactive', 'low-latency'],
                  tags: ['api', 'real-time', 'synchronous'],
                  icon: '⚡',
                  description: 'Immediate prediction API',
                  realWorldExample: 'OpenAI API, prediction endpoints'
                },
                {
                  id: 'comp8.5.2',
                  name: 'Batch Scoring Scripts',
                  purpose: 'Offline scoring',
                  function: 'Process large datasets offline',
                  cost: { low: 1, high: 100, unit: 'per run' },
                  compatibility: ['offline', 'bulk', 'cost-effective'],
                  tags: ['batch', 'offline', 'bulk'],
                  icon: '📦',
                  description: 'Process data in batches',
                  realWorldExample: 'Nightly scoring jobs'
                },
                {
                  id: 'comp8.5.3',
                  name: 'Streaming Inference',
                  purpose: 'Real-time stream processing',
                  function: 'Process continuous data streams',
                  cost: { low: 100, high: 1000, unit: 'per month' },
                  compatibility: ['streaming', 'real-time', 'continuous'],
                  tags: ['streaming', 'real-time', 'continuous'],
                  icon: '🌊',
                  description: 'Stream processing inference',
                  realWorldExample: 'Real-time fraud detection'
                },
                {
                  id: 'comp8.5.4',
                  name: 'On-device Execution',
                  purpose: 'Edge inference',
                  function: 'Run models on local devices',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['edge', 'mobile', 'offline', 'privacy'],
                  tags: ['on-device', 'edge', 'mobile', 'offline'],
                  icon: '📱',
                  description: 'On-device model execution',
                  realWorldExample: 'TensorFlow Lite, Core ML'
                }
              ]
            }
          ]
        },
        {
          id: 'layer9',
          name: 'Orchestration, Scheduling & Systems Integration',
          keyQuestion: 'Coordinating the model with infrastructure',
          subLayers: [
            {
              id: 'sl9.1',
              name: 'Workflow Orchestration',
              meaning: 'Managing DAGs of data and model tasks',
              components: [
                {
                  id: 'comp9.1.1',
                  name: 'Apache Airflow',
                  purpose: 'Workflow orchestration',
                  function: 'Schedule and monitor workflows',
                  cost: { low: 0, high: 2000, unit: 'per month' },
                  compatibility: ['batch', 'etl', 'orchestration'],
                  tags: ['airflow', 'workflow', 'scheduling'],
                  icon: '📅',
                  description: 'Apache Airflow workflow scheduler',
                  realWorldExample: 'Airbnb, Lyft use Airflow'
                },
                {
                  id: 'comp9.1.2',
                  name: 'Dagster',
                  purpose: 'Modern data orchestration',
                  function: 'Build data pipelines',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['data', 'orchestration', 'modern'],
                  tags: ['dagster', 'orchestration', 'data'],
                  icon: '📅',
                  description: 'Dagster orchestration',
                  realWorldExample: 'Modern data teams'
                },
                {
                  id: 'comp9.1.3',
                  name: 'Prefect',
                  purpose: 'Dataflow orchestration',
                  function: 'Build resilient data pipelines',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['data', 'orchestration', 'python'],
                  tags: ['prefect', 'orchestration', 'python'],
                  icon: '📅',
                  description: 'Prefect workflow orchestration',
                  realWorldExample: 'Python-native orchestration'
                },
                {
                  id: 'comp9.1.4',
                  name: 'Kubeflow Pipelines',
                  purpose: 'ML workflow on Kubernetes',
                  function: 'Manage ML pipelines on K8s',
                  cost: { low: 0, high: 5000, unit: 'per month' },
                  compatibility: ['kubernetes', 'mlops', 'pipelines'],
                  tags: ['kubeflow', 'mlops', 'kubernetes'],
                  icon: '🔀',
                  description: 'Kubeflow for ML pipelines',
                  realWorldExample: 'Cisco, Uber use Kubeflow'
                },
                {
                  id: 'comp9.1.5',
                  name: 'AWS Step Functions',
                  purpose: 'Serverless orchestration',
                  function: 'Coordinate AWS services',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['aws', 'serverless', 'orchestration'],
                  tags: ['aws', 'step-functions', 'serverless'],
                  icon: '☁️',
                  description: 'AWS Step Functions',
                  realWorldExample: 'Serverless workflow orchestration'
                }
              ]
            },
            {
              id: 'sl9.2',
              name: 'Container Orchestration',
              meaning: 'Automating deployment, scaling, networking',
              components: [
                {
                  id: 'comp9.2.1',
                  name: 'Kubernetes',
                  purpose: 'Container orchestration',
                  function: 'Automate deployment and scaling',
                  cost: { low: 0, high: 10000, unit: 'per month' },
                  compatibility: ['containers', 'scaling', 'cloud-native'],
                  tags: ['kubernetes', 'orchestration', 'containers'],
                  icon: '☸️',
                  description: 'Container orchestration platform',
                  realWorldExample: 'Google, Spotify use Kubernetes'
                },
                {
                  id: 'comp9.2.2',
                  name: 'Docker Swarm',
                  purpose: 'Simple container orchestration',
                  function: 'Orchestrate Docker containers',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['docker', 'simple', 'orchestration'],
                  tags: ['docker-swarm', 'orchestration', 'simple'],
                  icon: '🐳',
                  description: 'Docker Swarm orchestration',
                  realWorldExample: 'Simpler alternative to K8s'
                },
                {
                  id: 'comp9.2.3',
                  name: 'Nomad',
                  purpose: 'Workload orchestration',
                  function: 'Schedule containers and VMs',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['orchestration', 'simple', 'hashicorp'],
                  tags: ['nomad', 'orchestration', 'hashicorp'],
                  icon: '🏗️',
                  description: 'HashiCorp Nomad',
                  realWorldExample: 'Used by Cloudflare, Roblox'
                },
                {
                  id: 'comp9.2.4',
                  name: 'AWS ECS',
                  purpose: 'Managed container service',
                  function: 'Run containers on AWS',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['aws', 'containers', 'managed'],
                  tags: ['aws', 'ecs', 'containers', 'managed'],
                  icon: '☁️',
                  description: 'Amazon ECS container service',
                  realWorldExample: 'Container management on AWS'
                }
              ]
            },
            {
              id: 'sl9.3',
              name: 'Event & Queue Mgmt',
              meaning: 'Buffering requests between decoupled systems',
              components: [
                {
                  id: 'comp9.3.1',
                  name: 'Apache Kafka',
                  purpose: 'Event streaming platform',
                  function: 'Handle high-throughput event streams',
                  cost: { low: 0, high: 5000, unit: 'per month' },
                  compatibility: ['events', 'streaming', 'queue'],
                  tags: ['kafka', 'events', 'streaming'],
                  icon: '📨',
                  description: 'Apache Kafka event streaming',
                  realWorldExample: 'Used by LinkedIn, Netflix'
                },
                {
                  id: 'comp9.3.2',
                  name: 'RabbitMQ',
                  purpose: 'Message broker',
                  function: 'Route messages between services',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['async', 'microservices', 'queue'],
                  tags: ['rabbitmq', 'messaging', 'queue'],
                  icon: '🐰',
                  description: 'RabbitMQ message broker',
                  realWorldExample: 'Instagram, Reddit use RabbitMQ'
                },
                {
                  id: 'comp9.3.3',
                  name: 'AWS SQS',
                  purpose: 'Managed message queue',
                  function: 'Queue messages on AWS',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['aws', 'queue', 'managed'],
                  tags: ['aws', 'sqs', 'queue', 'managed'],
                  icon: '☁️',
                  description: 'Amazon Simple Queue Service',
                  realWorldExample: 'Decoupled services on AWS'
                },
                {
                  id: 'comp9.3.4',
                  name: 'Redis Streams',
                  purpose: 'In-memory event streams',
                  function: 'Stream data in Redis',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['redis', 'streaming', 'in-memory'],
                  tags: ['redis', 'streams', 'in-memory'],
                  icon: '⚡',
                  description: 'Redis Streams for event data',
                  realWorldExample: 'Real-time event processing'
                }
              ]
            },
            {
              id: 'sl9.4',
              name: 'CI/CD for ML',
              meaning: 'Automating testing and rollout of new models',
              components: [
                {
                  id: 'comp9.4.1',
                  name: 'GitHub Actions',
                  purpose: 'Automation workflows',
                  function: 'Automate ML pipeline testing',
                  cost: { low: 0, high: 50, unit: 'per month' },
                  compatibility: ['automation', 'testing', 'ci-cd'],
                  tags: ['github-actions', 'ci-cd', 'automation'],
                  icon: '🔄',
                  description: 'GitHub Actions CI/CD',
                  realWorldExample: 'Industry standard for CI/CD'
                },
                {
                  id: 'comp9.4.2',
                  name: 'Jenkins',
                  purpose: 'Automation server',
                  function: 'Build and deploy pipelines',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['ci-cd', 'automation', 'self-hosted'],
                  tags: ['jenkins', 'ci-cd', 'automation'],
                  icon: '🏗️',
                  description: 'Jenkins automation server',
                  realWorldExample: 'Used in enterprise environments'
                },
                {
                  id: 'comp9.4.3',
                  name: 'GitLab CI',
                  purpose: 'Integrated CI/CD',
                  function: 'Build and test in GitLab',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['gitlab', 'ci-cd', 'integrated'],
                  tags: ['gitlab', 'ci-cd', 'integrated'],
                  icon: '🔄',
                  description: 'GitLab CI/CD',
                  realWorldExample: 'GitLab repository automation'
                },
                {
                  id: 'comp9.4.4',
                  name: 'ArgoCD',
                  purpose: 'GitOps deployment',
                  function: 'Deploy to Kubernetes via Git',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['kubernetes', 'gitops', 'deployment'],
                  tags: ['argocd', 'gitops', 'kubernetes'],
                  icon: '🚢',
                  description: 'ArgoCD GitOps tool',
                  realWorldExample: 'Kubernetes deployments'
                }
              ]
            },
            {
              id: 'sl9.5',
              name: 'Serving Infrastructure',
              meaning: 'Network layer handling client requests',
              components: [
                {
                  id: 'comp9.5.1',
                  name: 'Nginx',
                  purpose: 'Reverse proxy and load balancer',
                  function: 'Route traffic to model servers',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['web', 'load-balancing', 'proxy'],
                  tags: ['nginx', 'proxy', 'load-balancer'],
                  icon: '🌐',
                  description: 'Nginx web server and proxy',
                  realWorldExample: 'Used by majority of web apps'
                },
                {
                  id: 'comp9.5.2',
                  name: 'Envoy Proxy',
                  purpose: 'Service mesh proxy',
                  function: 'Route service-to-service traffic',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['microservices', 'service-mesh', 'proxy'],
                  tags: ['envoy', 'proxy', 'service-mesh'],
                  icon: '🔀',
                  description: 'Envoy service proxy',
                  realWorldExample: 'Istio service mesh'
                },
                {
                  id: 'comp9.5.3',
                  name: 'API Gateway (Kong)',
                  purpose: 'API management',
                  function: 'Manage and secure APIs',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['api', 'gateway', 'management'],
                  tags: ['kong', 'api-gateway', 'management'],
                  icon: '🚪',
                  description: 'Kong API Gateway',
                  realWorldExample: 'API management for microservices'
                },
                {
                  id: 'comp9.5.4',
                  name: 'Load Balancer',
                  purpose: 'Distribute traffic',
                  function: 'Balance load across servers',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['scaling', 'availability', 'traffic'],
                  tags: ['load-balancer', 'scaling', 'availability'],
                  icon: '⚖️',
                  description: 'Load balancing infrastructure',
                  realWorldExample: 'AWS ELB, HAProxy'
                },
                {
                  id: 'comp9.5.5',
                  name: 'Serverless Functions (Lambda)',
                  purpose: 'Serverless compute',
                  function: 'Run code without servers',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['serverless', 'aws', 'event-driven'],
                  tags: ['lambda', 'serverless', 'aws'],
                  icon: '☁️',
                  description: 'AWS Lambda functions',
                  realWorldExample: 'Serverless ML inference'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase6',
      name: 'Interface Layer',
      description: 'How users interact with AI systems',
      icon: '🎯',
      layers: [
        {
          id: 'layer10',
          name: 'Application Logic & User/System Interface',
          keyQuestion: 'The "last mile" translating output to action',
          subLayers: [
            {
              id: 'sl10.1',
              name: 'Backend App Logic',
              meaning: 'Code that calls models and handles business rules',
              components: [
                {
                  id: 'comp10.1.1',
                  name: 'Python (FastAPI)',
                  purpose: 'Modern Python web framework',
                  function: 'Build API endpoints for models',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'api', 'async', 'ml'],
                  tags: ['fastapi', 'python', 'api', 'async'],
                  icon: '🐍',
                  description: 'FastAPI web framework',
                  realWorldExample: 'Netflix, Uber use FastAPI'
                },
                {
                  id: 'comp10.1.2',
                  name: 'Python (Django)',
                  purpose: 'Full-featured web framework',
                  function: 'Build complete web applications',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['python', 'web', 'full-stack'],
                  tags: ['django', 'python', 'web', 'full-stack'],
                  icon: '🐍',
                  description: 'Django web framework',
                  realWorldExample: 'Instagram uses Django'
                },
                {
                  id: 'comp10.1.3',
                  name: 'Node.js',
                  purpose: 'JavaScript backend',
                  function: 'Build scalable web services',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['javascript', 'web', 'real-time'],
                  tags: ['nodejs', 'javascript', 'web'],
                  icon: '🟢',
                  description: 'Node.js runtime',
                  realWorldExample: 'LinkedIn, PayPal use Node.js'
                },
                {
                  id: 'comp10.1.4',
                  name: 'Go',
                  purpose: 'High-performance backend',
                  function: 'Build efficient services',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['performance', 'concurrency', 'backend'],
                  tags: ['go', 'performance', 'concurrency'],
                  icon: '🐹',
                  description: 'Go programming language',
                  realWorldExample: 'Docker, Kubernetes are in Go'
                },
                {
                  id: 'comp10.1.5',
                  name: 'Business Logic Middleware',
                  purpose: 'Business rule enforcement',
                  function: 'Handle business logic between UI and ML',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['enterprise', 'business-rules', 'integration'],
                  tags: ['middleware', 'business-logic', 'enterprise'],
                  icon: '🔗',
                  description: 'Business logic middleware',
                  realWorldExample: 'Enterprise service bus'
                }
              ]
            },
            {
              id: 'sl10.2',
              name: 'Frontend & Visualization',
              meaning: 'Graphical interface for human interaction',
              components: [
                {
                  id: 'comp10.2.1',
                  name: 'React',
                  purpose: 'UI framework',
                  function: 'Build interactive user interfaces',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['web', 'interactive', 'spa', 'ui'],
                  tags: ['react', 'ui', 'javascript', 'web'],
                  icon: '⚛️',
                  description: 'React UI library',
                  realWorldExample: 'Facebook, Instagram use React'
                },
                {
                  id: 'comp10.2.2',
                  name: 'Vue.js',
                  purpose: 'Progressive UI framework',
                  function: 'Build user interfaces',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['web', 'ui', 'progressive'],
                  tags: ['vue', 'ui', 'javascript', 'web'],
                  icon: '💚',
                  description: 'Vue.js framework',
                  realWorldExample: 'Alibaba, Xiaomi use Vue'
                },
                {
                  id: 'comp10.2.3',
                  name: 'HTML/CSS',
                  purpose: 'Web markup and styling',
                  function: 'Create web pages',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['web', 'static', 'basic'],
                  tags: ['html', 'css', 'web', 'static'],
                  icon: '🌐',
                  description: 'HTML and CSS for web',
                  realWorldExample: 'Every website uses HTML/CSS'
                },
                {
                  id: 'comp10.2.4',
                  name: 'Streamlit',
                  purpose: 'Data app framework',
                  function: 'Quick ML demos and dashboards',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['data', 'dashboard', 'prototype', 'python'],
                  tags: ['streamlit', 'dashboard', 'python'],
                  icon: '📊',
                  description: 'Streamlit data applications',
                  realWorldExample: 'Uber, Twitter use Streamlit'
                },
                {
                  id: 'comp10.2.5',
                  name: 'Gradio',
                  purpose: 'ML demo framework',
                  function: 'Create ML model interfaces',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['ml', 'demo', 'prototype', 'python'],
                  tags: ['gradio', 'ml', 'demo', 'python'],
                  icon: '🎨',
                  description: 'Gradio for ML demos',
                  realWorldExample: 'Hugging Face Spaces use Gradio'
                },
                {
                  id: 'comp10.2.6',
                  name: 'Power BI',
                  purpose: 'Business intelligence',
                  function: 'Create business dashboards',
                  cost: { low: 10, high: 5000, unit: 'per month' },
                  compatibility: ['bi', 'dashboard', 'business', 'microsoft'],
                  tags: ['power-bi', 'bi', 'dashboard', 'microsoft'],
                  icon: '📊',
                  description: 'Power BI analytics',
                  realWorldExample: 'Enterprise business dashboards'
                },
                {
                  id: 'comp10.2.7',
                  name: 'Tableau',
                  purpose: 'Data visualization',
                  function: 'Create interactive visualizations',
                  cost: { low: 70, high: 2000, unit: 'per month' },
                  compatibility: ['visualization', 'bi', 'analytics'],
                  tags: ['tableau', 'visualization', 'bi'],
                  icon: '📈',
                  description: 'Tableau visualization',
                  realWorldExample: 'Used in enterprise analytics'
                },
                {
                  id: 'comp10.2.8',
                  name: 'Custom Dashboard',
                  purpose: 'Tailored visualization',
                  function: 'Build custom dashboards',
                  cost: { low: 1000, high: 50000, unit: 'per project' },
                  compatibility: ['custom', 'visualization', 'specific'],
                  tags: ['custom', 'dashboard', 'visualization'],
                  icon: '📊',
                  description: 'Custom dashboard development',
                  realWorldExample: 'D3.js, Chart.js custom dashboards'
                }
              ]
            },
            {
              id: 'sl10.3',
              name: 'Conversational Interface',
              meaning: 'Systems designed for natural language dialogue',
              components: [
                {
                  id: 'comp10.3.1',
                  name: 'Chatbot',
                  purpose: 'Text-based conversation',
                  function: 'Provide conversational UI',
                  cost: { low: 10, high: 500, unit: 'per month' },
                  compatibility: ['llm', 'customer-service', 'assistant'],
                  tags: ['chatbot', 'conversational', 'nlp'],
                  icon: '💬',
                  description: 'Chatbot interface',
                  realWorldExample: 'Intercom, Drift, custom chatbots'
                },
                {
                  id: 'comp10.3.2',
                  name: 'Voice Assistant',
                  purpose: 'Voice-based interaction',
                  function: 'Process voice commands',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['speech', 'assistant', 'voice', 'hands-free'],
                  tags: ['voice', 'speech', 'assistant'],
                  icon: '🎙️',
                  description: 'Voice-enabled assistant',
                  realWorldExample: 'Alexa Skills, Google Assistant'
                },
                {
                  id: 'comp10.3.3',
                  name: 'Copilot',
                  purpose: 'AI-assisted work',
                  function: 'Assist in content creation',
                  cost: { low: 10, high: 100, unit: 'per month' },
                  compatibility: ['llm', 'productivity', 'assistance'],
                  tags: ['copilot', 'assistance', 'productivity'],
                  icon: '🤖',
                  description: 'AI copilot for productivity',
                  realWorldExample: 'GitHub Copilot, Microsoft Copilot'
                }
              ]
            },
            {
              id: 'sl10.4',
              name: 'Autonomous Actuation',
              meaning: 'Direct connection to actuators without human review',
              components: [
                {
                  id: 'comp10.4.1',
                  name: 'Robotic Control System',
                  purpose: 'Control robots autonomously',
                  function: 'Execute robotic actions',
                  cost: { low: 1000, high: 100000, unit: 'per system' },
                  compatibility: ['robotics', 'autonomous', 'control'],
                  tags: ['robotics', 'autonomous', 'control'],
                  icon: '🦾',
                  description: 'Robotic control system',
                  realWorldExample: 'ROS-based control systems'
                },
                {
                  id: 'comp10.4.2',
                  name: 'Automated Trading Algorithm',
                  purpose: 'Automated financial trading',
                  function: 'Execute trades based on signals',
                  cost: { low: 100, high: 10000, unit: 'per month' },
                  compatibility: ['finance', 'automation', 'real-time'],
                  tags: ['trading', 'finance', 'automation'],
                  icon: '📈',
                  description: 'Automated trading system',
                  realWorldExample: 'Quant funds, crypto trading bots'
                },
                {
                  id: 'comp10.4.3',
                  name: 'Autonomous Vehicle Path Planner',
                  purpose: 'Autonomous navigation',
                  function: 'Plan vehicle paths',
                  cost: { low: 1000, high: 100000, unit: 'per system' },
                  compatibility: ['autonomous', 'vehicles', 'navigation'],
                  tags: ['autonomous', 'vehicle', 'navigation'],
                  icon: '🚗',
                  description: 'Autonomous vehicle planning',
                  realWorldExample: 'Tesla Autopilot, Waymo'
                },
                {
                  id: 'comp10.4.4',
                  name: 'Agentic Tool Usage',
                  purpose: 'Autonomous tool execution',
                  function: 'AI agents using tools',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['llm', 'agents', 'automation'],
                  tags: ['agents', 'tools', 'automation'],
                  icon: '🛠️',
                  description: 'AI agent tool usage',
                  realWorldExample: 'AutoGPT, LangChain agents'
                }
              ]
            },
            {
              id: 'sl10.5',
              name: 'Personalization & Context Logic',
              meaning: 'Tailoring user experience based on state',
              components: [
                {
                  id: 'comp10.5.1',
                  name: 'Recommendation Carousel',
                  purpose: 'Display personalized items',
                  function: 'Show recommended products',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['ecommerce', 'personalization', 'recommendation'],
                  tags: ['recommendation', 'personalization', 'carousel'],
                  icon: '🎠',
                  description: 'Recommendation carousel UI',
                  realWorldExample: 'Netflix rows, Amazon recommendations'
                },
                {
                  id: 'comp10.5.2',
                  name: 'Session Manager',
                  purpose: 'Manage user sessions',
                  function: 'Track user state and context',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['web', 'state', 'user-context'],
                  tags: ['session', 'state', 'user'],
                  icon: '🔐',
                  description: 'Session management',
                  realWorldExample: 'Redis sessions, JWT tokens'
                },
                {
                  id: 'comp10.5.3',
                  name: 'A/B Testing Frame',
                  purpose: 'Experiment with variants',
                  function: 'Test different versions',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['testing', 'optimization', 'experiment'],
                  tags: ['a-b-testing', 'experiment', 'optimization'],
                  icon: '🧪',
                  description: 'A/B testing framework',
                  realWorldExample: 'Optimizely, Google Optimize'
                },
                {
                  id: 'comp10.5.4',
                  name: 'Feature Flags',
                  purpose: 'Control feature rollout',
                  function: 'Enable/disable features',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['deployment', 'rollout', 'control'],
                  tags: ['feature-flags', 'rollout', 'control'],
                  icon: '🚩',
                  description: 'Feature flag management',
                  realWorldExample: 'LaunchDarkly, feature flags'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'phase7',
      name: 'Meta Layer',
      description: 'Oversight, governance, and safety',
      icon: '🛡️',
      layers: [
        {
          id: 'layer11',
          name: 'Observability, Evaluation & Security',
          keyQuestion: 'Monitoring system health, performance, and safety',
          subLayers: [
            {
              id: 'sl11.1',
              name: 'Performance Monitoring',
              meaning: 'Tracking latency, throughput, and health',
              components: [
                {
                  id: 'comp11.1.1',
                  name: 'Prometheus',
                  purpose: 'Metrics collection',
                  function: 'Collect and store metrics',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['monitoring', 'metrics', 'alerting'],
                  tags: ['prometheus', 'metrics', 'monitoring'],
                  icon: '📊',
                  description: 'Prometheus monitoring',
                  realWorldExample: 'SoundCloud, DigitalOcean use Prometheus'
                },
                {
                  id: 'comp11.1.2',
                  name: 'Grafana',
                  purpose: 'Visualization dashboards',
                  function: 'Create monitoring dashboards',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['dashboards', 'visualization', 'alerts'],
                  tags: ['grafana', 'dashboard', 'visualization'],
                  icon: '📈',
                  description: 'Grafana dashboards',
                  realWorldExample: 'eBay, PayPal use Grafana'
                },
                {
                  id: 'comp11.1.3',
                  name: 'Datadog',
                  purpose: 'Cloud monitoring',
                  function: 'Monitor cloud infrastructure',
                  cost: { low: 15, high: 5000, unit: 'per month' },
                  compatibility: ['cloud', 'monitoring', 'apm'],
                  tags: ['datadog', 'monitoring', 'cloud', 'apm'],
                  icon: '🐕',
                  description: 'Datadog monitoring platform',
                  realWorldExample: 'SaaS monitoring for cloud'
                },
                {
                  id: 'comp11.1.4',
                  name: 'New Relic',
                  purpose: 'Application performance',
                  function: 'Monitor application performance',
                  cost: { low: 0, high: 2000, unit: 'per month' },
                  compatibility: ['apm', 'monitoring', 'performance'],
                  tags: ['new-relic', 'apm', 'monitoring'],
                  icon: '📊',
                  description: 'New Relic APM',
                  realWorldExample: 'Application performance monitoring'
                },
                {
                  id: 'comp11.1.5',
                  name: 'CloudWatch',
                  purpose: 'AWS monitoring',
                  function: 'Monitor AWS resources',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['aws', 'monitoring', 'cloud'],
                  tags: ['cloudwatch', 'aws', 'monitoring'],
                  icon: '☁️',
                  description: 'Amazon CloudWatch',
                  realWorldExample: 'AWS resource monitoring'
                }
              ]
            },
            {
              id: 'sl11.2',
              name: 'Model Quality & Drift Monitoring',
              meaning: 'Detecting decay in accuracy or shifts in data',
              components: [
                {
                  id: 'comp11.2.1',
                  name: 'Evidently AI',
                  purpose: 'Model monitoring',
                  function: 'Detect data and concept drift',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['mlops', 'drift', 'quality'],
                  tags: ['evidently', 'drift', 'monitoring'],
                  icon: '📉',
                  description: 'Evidently AI monitoring',
                  realWorldExample: 'Drift detection for ML models'
                },
                {
                  id: 'comp11.2.2',
                  name: 'WhyLabs',
                  purpose: 'AI observability',
                  function: 'Monitor data quality and drift',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['observability', 'drift', 'quality'],
                  tags: ['whylabs', 'observability', 'drift'],
                  icon: '🔍',
                  description: 'WhyLabs AI Observatory',
                  realWorldExample: 'Used by enterprise ML teams'
                },
                {
                  id: 'comp11.2.3',
                  name: 'Fiddler AI',
                  purpose: 'Model explainability and monitoring',
                  function: 'Monitor and explain models',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['explainability', 'monitoring', 'mlops'],
                  tags: ['fiddler', 'explainability', 'monitoring'],
                  icon: '🎻',
                  description: 'Fiddler AI platform',
                  realWorldExample: 'Enterprise model monitoring'
                },
                {
                  id: 'comp11.2.4',
                  name: 'KS-Test',
                  purpose: 'Statistical drift detection',
                  function: 'Compare distributions statistically',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['statistics', 'drift', 'detection'],
                  tags: ['ks-test', 'statistics', 'drift'],
                  icon: '📊',
                  description: 'Kolmogorov-Smirnov test',
                  realWorldExample: 'Statistical drift detection'
                },
                {
                  id: 'comp11.2.5',
                  name: 'Population Stability Index',
                  purpose: 'Distribution shift measurement',
                  function: 'Measure population shift',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['drift', 'statistics', 'monitoring'],
                  tags: ['psi', 'drift', 'statistics'],
                  icon: '📈',
                  description: 'Population Stability Index',
                  realWorldExample: 'Credit scoring model monitoring'
                }
              ]
            },
            {
              id: 'sl11.3',
              name: 'Explainability (XAI)',
              meaning: 'Extracting human-understandable reasons',
              components: [
                {
                  id: 'comp11.3.1',
                  name: 'SHAP',
                  purpose: 'Feature importance',
                  function: 'Explain individual predictions',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['explainability', 'audit', 'trust'],
                  tags: ['shap', 'explainability', 'importance'],
                  icon: '🔍',
                  description: 'SHapley Additive exPlanations',
                  realWorldExample: 'Healthcare, finance model explanation'
                },
                {
                  id: 'comp11.3.2',
                  name: 'LIME',
                  purpose: 'Local interpretability',
                  function: 'Explain predictions locally',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['explainability', 'local', 'interpretable'],
                  tags: ['lime', 'explainability', 'local'],
                  icon: '🍋',
                  description: 'Local Interpretable Model-agnostic Explanations',
                  realWorldExample: 'Used for model debugging'
                },
                {
                  id: 'comp11.3.3',
                  name: 'Integrated Gradients',
                  purpose: 'Neural network attribution',
                  function: 'Attribute predictions to inputs',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['neural-networks', 'attribution', 'explainability'],
                  tags: ['integrated-gradients', 'attribution', 'explainability'],
                  icon: '📐',
                  description: 'Integrated Gradients method',
                  realWorldExample: 'Used in computer vision XAI'
                },
                {
                  id: 'comp11.3.4',
                  name: 'Attention Visualization',
                  purpose: 'Visualize model attention',
                  function: 'Show attention weights',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['transformers', 'attention', 'visualization'],
                  tags: ['attention', 'visualization', 'transformers'],
                  icon: '👁️',
                  description: 'Attention visualization',
                  realWorldExample: 'BERT attention heatmaps'
                }
              ]
            },
            {
              id: 'sl11.4',
              name: 'Security & Adversarial Defense',
              meaning: 'Protecting model and data from malicious intent',
              components: [
                {
                  id: 'comp11.4.1',
                  name: 'Adversarial Training',
                  purpose: 'Robustness to attacks',
                  function: 'Train with adversarial examples',
                  cost: { low: 0, high: 1000, unit: 'per training' },
                  compatibility: ['security', 'robustness', 'training'],
                  tags: ['adversarial', 'security', 'robustness'],
                  icon: '🛡️',
                  description: 'Adversarial training',
                  realWorldExample: 'Robust models against attacks'
                },
                {
                  id: 'comp11.4.2',
                  name: 'Prompt Injection Firewall',
                  purpose: 'Protect LLMs',
                  function: 'Block malicious prompts',
                  cost: { low: 100, high: 1000, unit: 'per month' },
                  compatibility: ['llm', 'security', 'prompting'],
                  tags: ['prompt-injection', 'llm', 'security'],
                  icon: '🔥',
                  description: 'Prompt injection protection',
                  realWorldExample: 'LLM security filters'
                },
                {
                  id: 'comp11.4.3',
                  name: 'Data Encryption (TLS/AES)',
                  purpose: 'Data security',
                  function: 'Encrypt data in transit and at rest',
                  cost: { low: 0, high: 100, unit: 'per year' },
                  compatibility: ['security', 'compliance', 'all'],
                  tags: ['encryption', 'tls', 'aes', 'security'],
                  icon: '🔒',
                  description: 'Data encryption',
                  realWorldExample: 'SSL/TLS certificates, AES-256'
                },
                {
                  id: 'comp11.4.4',
                  name: 'Rate Limiting',
                  purpose: 'Prevent abuse',
                  function: 'Limit request rates',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['api', 'security', 'abuse-prevention'],
                  tags: ['rate-limiting', 'security', 'api'],
                  icon: '🚦',
                  description: 'Rate limiting system',
                  realWorldExample: 'API rate limiting'
                },
                {
                  id: 'comp11.4.5',
                  name: 'WAF',
                  purpose: 'Web application firewall',
                  function: 'Protect web applications',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['web', 'security', 'firewall'],
                  tags: ['waf', 'firewall', 'security', 'web'],
                  icon: '🛡️',
                  description: 'Web Application Firewall',
                  realWorldExample: 'AWS WAF, Cloudflare WAF'
                }
              ]
            },
            {
              id: 'sl11.5',
              name: 'Debugging & Tracing',
              meaning: 'Isolating root cause of failures',
              components: [
                {
                  id: 'comp11.5.1',
                  name: 'OpenTelemetry',
                  purpose: 'Distributed tracing',
                  function: 'Trace requests across services',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['observability', 'tracing', 'microservices'],
                  tags: ['opentelemetry', 'tracing', 'observability'],
                  icon: '🔎',
                  description: 'OpenTelemetry framework',
                  realWorldExample: 'CNCF project for observability'
                },
                {
                  id: 'comp11.5.2',
                  name: 'Sentry',
                  purpose: 'Error tracking',
                  function: 'Track and report errors',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['error-tracking', 'debugging', 'monitoring'],
                  tags: ['sentry', 'errors', 'debugging'],
                  icon: '🐛',
                  description: 'Sentry error tracking',
                  realWorldExample: 'Used by development teams'
                },
                {
                  id: 'comp11.5.3',
                  name: 'Log Aggregation (ELK)',
                  purpose: 'Centralized logging',
                  function: 'Aggregate and search logs',
                  cost: { low: 0, high: 1000, unit: 'per month' },
                  compatibility: ['logging', 'debugging', 'observability'],
                  tags: ['elk', 'logging', 'elasticsearch'],
                  icon: '📋',
                  description: 'ELK Stack for logging',
                  realWorldExample: 'Elasticsearch, Logstash, Kibana'
                },
                {
                  id: 'comp11.5.4',
                  name: 'Data Lineage Tools',
                  purpose: 'Track data flow',
                  function: 'Track data origin and transformations',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['data-governance', 'lineage', 'observability'],
                  tags: ['lineage', 'data-governance', 'tracking'],
                  icon: '🔗',
                  description: 'Data lineage tracking',
                  realWorldExample: 'Apache Atlas, Alation'
                }
              ]
            }
          ]
        },
        {
          id: 'layer12',
          name: 'Governance, Policy & Human-in-the-Loop',
          keyQuestion: 'Aligning with business, legal, and ethical realities',
          subLayers: [
            {
              id: 'sl12.1',
              name: 'Access Control & Identity',
              meaning: 'Managing who can access AI resources',
              components: [
                {
                  id: 'comp12.1.1',
                  name: 'SSO (Okta)',
                  purpose: 'Single sign-on',
                  function: 'Centralize authentication',
                  cost: { low: 2, high: 15, unit: 'per user/month' },
                  compatibility: ['enterprise', 'authentication', 'security'],
                  tags: ['sso', 'okta', 'authentication'],
                  icon: '🔐',
                  description: 'Okta single sign-on',
                  realWorldExample: 'Enterprise authentication'
                },
                {
                  id: 'comp12.1.2',
                  name: 'OAuth 2.0',
                  purpose: 'Authorization framework',
                  function: 'Secure API access',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['security', 'api', 'authentication'],
                  tags: ['oauth', 'authorization', 'security'],
                  icon: '🔑',
                  description: 'OAuth 2.0 authorization',
                  realWorldExample: 'Google, Facebook login'
                },
                {
                  id: 'comp12.1.3',
                  name: 'RBAC',
                  purpose: 'Role-based access control',
                  function: 'Control access by role',
                  cost: { low: 0, high: 100, unit: 'per month' },
                  compatibility: ['security', 'access-control', 'enterprise'],
                  tags: ['rbac', 'access-control', 'roles'],
                  icon: '👥',
                  description: 'Role-Based Access Control',
                  realWorldExample: 'Kubernetes RBAC'
                },
                {
                  id: 'comp12.1.4',
                  name: 'Network Segmentation',
                  purpose: 'Isolate network resources',
                  function: 'Segment network for security',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['security', 'network', 'isolation'],
                  tags: ['segmentation', 'network', 'security'],
                  icon: '🌐',
                  description: 'Network segmentation',
                  realWorldExample: 'VLANs, VPCs'
                },
                {
                  id: 'comp12.1.5',
                  name: 'Air-gapping',
                  purpose: 'Physical network isolation',
                  function: 'Isolate critical systems',
                  cost: { low: 1000, high: 100000, unit: 'per system' },
                  compatibility: ['security', 'isolation', 'critical'],
                  tags: ['air-gap', 'isolation', 'security'],
                  icon: '🔒',
                  description: 'Air-gapped systems',
                  realWorldExample: 'Military, critical infrastructure'
                }
              ]
            },
            {
              id: 'sl12.2',
              name: 'Audit & Provenance',
              meaning: 'Immutable logs tracking data and decisions',
              components: [
                {
                  id: 'comp12.2.1',
                  name: 'Blockchain Timestamps',
                  purpose: 'Immutable timestamps',
                  function: 'Record immutable timestamps',
                  cost: { low: 10, high: 1000, unit: 'per month' },
                  compatibility: ['audit', 'immutable', 'blockchain'],
                  tags: ['blockchain', 'immutable', 'audit'],
                  icon: '⛓️',
                  description: 'Blockchain-based timestamps',
                  realWorldExample: 'OpenTimestamps, blockchain notarization'
                },
                {
                  id: 'comp12.2.2',
                  name: 'Model Cards',
                  purpose: 'Model documentation',
                  function: 'Document model details',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['documentation', 'transparency', 'compliance'],
                  tags: ['model-cards', 'documentation', 'transparency'],
                  icon: '📋',
                  description: 'Model card documentation',
                  realWorldExample: 'Google Model Card Toolkit'
                },
                {
                  id: 'comp12.2.3',
                  name: 'Data Cards',
                  purpose: 'Dataset documentation',
                  function: 'Document dataset details',
                  cost: { low: 0, high: 0, unit: 'free' },
                  compatibility: ['documentation', 'transparency', 'data'],
                  tags: ['data-cards', 'documentation', 'data'],
                  icon: '📄',
                  description: 'Data card documentation',
                  realWorldExample: 'Dataset transparency'
                },
                {
                  id: 'comp12.2.4',
                  name: 'Git Version History',
                  purpose: 'Version control',
                  function: 'Track changes over time',
                  cost: { low: 0, high: 20, unit: 'per month' },
                  compatibility: ['versioning', 'audit', 'development'],
                  tags: ['git', 'versioning', 'audit'],
                  icon: '📚',
                  description: 'Git version control',
                  realWorldExample: 'GitHub, GitLab repositories'
                },
                {
                  id: 'comp12.2.5',
                  name: 'Audit Logs (SIEM)',
                  purpose: 'Security event logging',
                  function: 'Collect and analyze security events',
                  cost: { low: 100, high: 10000, unit: 'per month' },
                  compatibility: ['security', 'audit', 'compliance'],
                  tags: ['siem', 'audit', 'security'],
                  icon: '📊',
                  description: 'SIEM audit logging',
                  realWorldExample: 'Splunk, IBM QRadar'
                }
              ]
            },
            {
              id: 'sl12.3',
              name: 'Human Review Systems',
              meaning: 'Interfaces for humans to verify AI output',
              components: [
                {
                  id: 'comp12.3.1',
                  name: 'Review Queue (Content Moderation)',
                  purpose: 'Content moderation',
                  function: 'Review AI-flagged content',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['content-moderation', 'human-review', 'safety'],
                  tags: ['review-queue', 'moderation', 'human'],
                  icon: '👁️',
                  description: 'Content review queue',
                  realWorldExample: 'Facebook, YouTube moderation tools'
                },
                {
                  id: 'comp12.3.2',
                  name: 'Doctor-in-the-loop Dashboard',
                  purpose: 'Medical AI oversight',
                  function: 'Review medical AI decisions',
                  cost: { low: 1000, high: 10000, unit: 'per month' },
                  compatibility: ['medical', 'oversight', 'healthcare'],
                  tags: ['medical', 'oversight', 'healthcare'],
                  icon: '⚕️',
                  description: 'Medical AI review dashboard',
                  realWorldExample: 'Radiology AI review systems'
                },
                {
                  id: 'comp12.3.3',
                  name: 'Kill Switch',
                  purpose: 'Emergency shutdown',
                  function: 'Immediately stop AI system',
                  cost: { low: 100, high: 1000, unit: 'per system' },
                  compatibility: ['safety', 'emergency', 'control'],
                  tags: ['kill-switch', 'emergency', 'safety'],
                  icon: '🛑',
                  description: 'Emergency kill switch',
                  realWorldExample: 'Industrial AI safety systems'
                },
                {
                  id: 'comp12.3.4',
                  name: 'Escalation Path',
                  purpose: 'Escalate complex cases',
                  function: 'Route to human experts',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['customer-service', 'escalation', 'human'],
                  tags: ['escalation', 'human', 'support'],
                  icon: '📞',
                  description: 'Escalation to human agents',
                  realWorldExample: 'Chatbot to human handoff'
                }
              ]
            },
            {
              id: 'sl12.4',
              name: 'Compliance & Policy Engines',
              meaning: 'Mapping technical metrics to legal requirements',
              components: [
                {
                  id: 'comp12.4.1',
                  name: 'EU AI Act Checklist',
                  purpose: 'EU regulatory compliance',
                  function: 'Map to EU AI Act requirements',
                  cost: { low: 0, high: 1000, unit: 'per audit' },
                  compatibility: ['eu', 'regulation', 'compliance'],
                  tags: ['eu-ai-act', 'compliance', 'regulation'],
                  icon: '⚖️',
                  description: 'EU AI Act compliance',
                  realWorldExample: 'European AI compliance'
                },
                {
                  id: 'comp12.4.2',
                  name: 'SOC2 Compliance',
                  purpose: 'Security compliance',
                  function: 'Meet SOC2 requirements',
                  cost: { low: 1000, high: 50000, unit: 'per audit' },
                  compatibility: ['security', 'compliance', 'enterprise'],
                  tags: ['soc2', 'compliance', 'security'],
                  icon: '📋',
                  description: 'SOC2 compliance framework',
                  realWorldExample: 'Enterprise security compliance'
                },
                {
                  id: 'comp12.4.3',
                  name: 'HIPAA Safeguards',
                  purpose: 'Healthcare compliance',
                  function: 'Meet HIPAA requirements',
                  cost: { low: 1000, high: 50000, unit: 'per implementation' },
                  compatibility: ['healthcare', 'compliance', 'medical'],
                  tags: ['hipaa', 'healthcare', 'compliance'],
                  icon: '🏥',
                  description: 'HIPAA compliance',
                  realWorldExample: 'Healthcare data protection'
                },
                {
                  id: 'comp12.4.4',
                  name: 'Bias Audit Reports',
                  purpose: 'Algorithmic bias assessment',
                  function: 'Audit for bias',
                  cost: { low: 1000, high: 20000, unit: 'per audit' },
                  compatibility: ['fairness', 'audit', 'compliance'],
                  tags: ['bias', 'audit', 'fairness'],
                  icon: '⚖️',
                  description: 'Bias audit reporting',
                  realWorldExample: 'Algorithmic fairness audits'
                },
                {
                  id: 'comp12.4.5',
                  name: 'FinOps Budgets',
                  purpose: 'Cost management',
                  function: 'Manage AI spending',
                  cost: { low: 0, high: 500, unit: 'per month' },
                  compatibility: ['cost', 'budget', 'optimization'],
                  tags: ['finops', 'budget', 'cost'],
                  icon: '💰',
                  description: 'FinOps budget management',
                  realWorldExample: 'Cloud cost optimization'
                }
              ]
            },
            {
              id: 'sl12.5',
              name: 'Business Strategy & Portfolio Mgmt',
              meaning: 'Deciding which models to build, buy, or retire',
              components: [
                {
                  id: 'comp12.5.1',
                  name: 'AI Portfolio Dashboard',
                  purpose: 'Portfolio overview',
                  function: 'Visualize AI investments',
                  cost: { low: 100, high: 5000, unit: 'per month' },
                  compatibility: ['strategy', 'portfolio', 'management'],
                  tags: ['portfolio', 'strategy', 'dashboard'],
                  icon: '📊',
                  description: 'AI portfolio management',
                  realWorldExample: 'Enterprise AI portfolio tools'
                },
                {
                  id: 'comp12.5.2',
                  name: 'Cost-to-Serve Analytics',
                  purpose: 'Cost analysis',
                  function: 'Analyze serving costs',
                  cost: { low: 100, high: 1000, unit: 'per month' },
                  compatibility: ['cost', 'analytics', 'optimization'],
                  tags: ['cost', 'analytics', 'serving'],
                  icon: '💰',
                  description: 'Cost-to-serve analytics',
                  realWorldExample: 'Cloud cost per prediction'
                },
                {
                  id: 'comp12.5.3',
                  name: 'Model Lifecycle Management',
                  purpose: 'Model lifecycle',
                  function: 'Manage model from creation to retirement',
                  cost: { low: 500, high: 5000, unit: 'per month' },
                  compatibility: ['lifecycle', 'management', 'mlops'],
                  tags: ['lifecycle', 'management', 'mlops'],
                  icon: '🔄',
                  description: 'Model lifecycle management',
                  realWorldExample: 'MLflow, SageMaker lifecycle'
                }
              ]
            }
          ]
        }
      ]
    }

    // Note: This file continues with Phases 3-7 (Layers 5-12)
    // Due to length, I'll continue in the next response
  ]
};

export const getComponentById = (id) => {
  return getAllComponents().find(c => c.id === id);
};

export const getLayerById = (id) => {
  for (const phase of AI_ONTOLOGY.phases) {
    for (const layer of phase.layers) {
      if (layer.id === id) return layer;
    }
  }
  return null;
};

export const getPhaseById = (id) => {
  return AI_ONTOLOGY.phases.find(p => p.id === id);
};

export const getSubLayerById = (id) => {
  for (const phase of AI_ONTOLOGY.phases) {
    for (const layer of phase.layers) {
      for (const subLayer of layer.subLayers) {
        if (subLayer.id === id) return subLayer;
      }
    }
  }
  return null;
};

export const getAllComponents = () => {
  const components = [];
  
  AI_ONTOLOGY.phases.forEach(phase => {
    phase.layers.forEach(layer => {
      layer.subLayers.forEach(subLayer => {
        subLayer.components.forEach(component => {
          components.push({
            ...component,
            // Explicitly add all hierarchical references
            phaseId: phase.id,
            phaseName: phase.name,
            phaseIcon: phase.icon,
            phaseDescription: phase.description,
            layerId: layer.id,
            layerName: layer.name,
            layerKeyQuestion: layer.keyQuestion,
            subLayerId: subLayer.id,
            subLayerName: subLayer.name,
            subLayerMeaning: subLayer.meaning
          });
        });
      });
    });
  });
  
  return components;
};


// Also add helper functions to get names by ID
export const getPhaseNameById = (phaseId) => {
  const phase = AI_ONTOLOGY.phases.find(p => p.id === phaseId);
  return phase ? phase.name : 'Unknown Phase';
};

export const getLayerNameById = (layerId) => {
  for (const phase of AI_ONTOLOGY.phases) {
    const layer = phase.layers.find(l => l.id === layerId);
    if (layer) return layer.name;
  }
  return 'Unknown Layer';
};

export const getSubLayerNameById = (subLayerId) => {
  for (const phase of AI_ONTOLOGY.phases) {
    for (const layer of phase.layers) {
      const subLayer = layer.subLayers.find(sl => sl.id === subLayerId);
      if (subLayer) return subLayer.name;
    }
  }
  return 'Unknown Sub-Layer';
};

// Name resolution functions
export const getPhaseName = (phaseId) => {
  if (!phaseId) return 'Unknown Phase';
  const phase = AI_ONTOLOGY.phases.find(p => p.id === phaseId);
  return phase ? phase.name : 'Unknown Phase';
};

export const getLayerName = (layerId) => {
  if (!layerId) return 'Unknown Layer';
  for (const phase of AI_ONTOLOGY.phases) {
    const layer = phase.layers.find(l => l.id === layerId);
    if (layer) return layer.name;
  }
  return 'Unknown Layer';
};

export const getSubLayerName = (subLayerId) => {
  if (!subLayerId) return 'Unknown Sub-Layer';
  for (const phase of AI_ONTOLOGY.phases) {
    for (const layer of phase.layers) {
      const subLayer = layer.subLayers.find(sl => sl.id === subLayerId);
      if (subLayer) return subLayer.name;
    }
  }
  return 'Unknown Sub-Layer';
};
